export interface Question {
  questionNumber: number;
  question: string;
  answerOptions: {
    text: string;
    rationale: string;
    isCorrect: boolean;
  }[];
  hint: string;
}

export const practiceTestQuestions: Question[] = [
  // BATCH 1: Texas Real Estate Commission & Licensing (Questions 1-20)
  {
    "questionNumber": 1,
    "question": "What is the primary purpose of the Texas Real Estate Commission (TREC)?",
    "answerOptions": [
      {
        "text": "To protect licensed real estate brokers from competition",
        "rationale": "TREC's purpose is to protect the public, not to protect licensees from competition.",
        "isCorrect": false
      },
      {
        "text": "To regulate property values and ensure stable housing prices",
        "rationale": "TREC regulates real estate professionals and activities, not property values.",
        "isCorrect": false
      },
      {
        "text": "To protect the public from dishonest or incompetent real estate professionals",
        "rationale": "TREC's primary purpose is to protect consumers by ensuring licensees meet ethical and competency standards.",
        "isCorrect": true
      },
      {
        "text": "To provide financing for real estate transactions",
        "rationale": "TREC does not provide financing; it regulates real estate professionals.",
        "isCorrect": false
      }
    ],
    "hint": "Think about consumer protection and professional regulation."
  },
  {
    "questionNumber": 2,
    "question": "How often must Texas real estate licenses be renewed?",
    "answerOptions": [
      {
        "text": "Annually",
        "rationale": "Texas licenses are renewed every two years, not annually.",
        "isCorrect": false
      },
      {
        "text": "Every two years",
        "rationale": "Texas real estate licenses must be renewed every two years.",
        "isCorrect": true
      },
      {
        "text": "Every three years",
        "rationale": "Texas licenses are renewed every two years, not three.",
        "isCorrect": false
      },
      {
        "text": "Every five years",
        "rationale": "Texas licenses are renewed every two years, not five.",
        "isCorrect": false
      }
    ],
    "hint": "Consider the standard renewal period for professional licenses."
  },
  {
    "questionNumber": 3,
    "question": "What is the minimum age requirement to obtain a Texas real estate license?",
    "answerOptions": [
      {
        "text": "16 years old",
        "rationale": "The minimum age is 18, not 16.",
        "isCorrect": false
      },
      {
        "text": "18 years old",
        "rationale": "Applicants must be at least 18 years old to obtain a Texas real estate license.",
        "isCorrect": true
      },
      {
        "text": "21 years old",
        "rationale": "The minimum age is 18, not 21.",
        "isCorrect": false
      },
      {
        "text": "25 years old",
        "rationale": "The minimum age is 18, not 25.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the legal age of majority in most states."
  },
  {
    "questionNumber": 4,
    "question": "What is required for Texas license renewal?",
    "answerOptions": [
      {
        "text": "Only payment of renewal fees",
        "rationale": "Continuing education is also required for renewal.",
        "isCorrect": false
      },
      {
        "text": "Continuing education and payment of renewal fees",
        "rationale": "Both continuing education and renewal fees are required for Texas license renewal.",
        "isCorrect": true
      },
      {
        "text": "Only continuing education",
        "rationale": "Renewal fees are also required.",
        "isCorrect": false
      },
      {
        "text": "No requirements other than application",
        "rationale": "Both continuing education and fees are required.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what maintains professional competency and covers administrative costs."
  },
  {
    "questionNumber": 5,
    "question": "What is a Texas broker-salesperson?",
    "answerOptions": [
      {
        "text": "A person who holds both broker and salesperson licenses",
        "rationale": "A broker-salesperson holds both licenses but chooses to work under another broker.",
        "isCorrect": true
      },
      {
        "text": "A salesperson who works independently",
        "rationale": "Salespersons must work under a broker's supervision.",
        "isCorrect": false
      },
      {
        "text": "A broker who employs salespersons",
        "rationale": "This describes a supervising broker, not a broker-salesperson.",
        "isCorrect": false
      },
      {
        "text": "A person with no license",
        "rationale": "A broker-salesperson holds both broker and salesperson licenses.",
        "isCorrect": false
      }
    ],
    "hint": "Think about someone who has broker qualifications but chooses to work under supervision."
  },
  {
    "questionNumber": 6,
    "question": "What is the difference between a Texas broker and a Texas salesperson?",
    "answerOptions": [
      {
        "text": "There is no difference; the terms are interchangeable",
        "rationale": "Brokers and salespersons have different qualifications and responsibilities.",
        "isCorrect": false
      },
      {
        "text": "Brokers can operate independently; salespersons must work under broker supervision",
        "rationale": "Brokers can operate independently and employ salespersons, while salespersons must work under broker supervision.",
        "isCorrect": true
      },
      {
        "text": "Salespersons earn more commission than brokers",
        "rationale": "Commission rates are not determined by license type.",
        "isCorrect": false
      },
      {
        "text": "Brokers only work with commercial properties",
        "rationale": "Both brokers and salespersons can work with all types of properties.",
        "isCorrect": false
      }
    ],
    "hint": "Consider the supervisory relationship and independence levels."
  },
  {
    "questionNumber": 7,
    "question": "What is required to become a Texas real estate broker?",
    "answerOptions": [
      {
        "text": "Only passing the broker examination",
        "rationale": "Additional requirements include education and experience.",
        "isCorrect": false
      },
      {
        "text": "Being a licensed salesperson for at least 2 years and completing broker education",
        "rationale": "Broker applicants must have salesperson experience and complete broker-specific education.",
        "isCorrect": true
      },
      {
        "text": "Only completing broker education courses",
        "rationale": "Experience as a salesperson is also required.",
        "isCorrect": false
      },
      {
        "text": "Only having a college degree",
        "rationale": "A college degree is not required for broker licensure.",
        "isCorrect": false
      }
    ],
    "hint": "Consider the progression from salesperson to broker and the experience requirement."
  },
  {
    "questionNumber": 8,
    "question": "What is a Texas associate broker?",
    "answerOptions": [
      {
        "text": "A salesperson who works under a broker",
        "rationale": "An associate broker holds a broker license but works under another broker.",
        "isCorrect": false
      },
      {
        "text": "A broker who works under another broker's supervision",
        "rationale": "An associate broker has broker qualifications but chooses to work under another broker's supervision.",
        "isCorrect": true
      },
      {
        "text": "A broker who employs salespersons",
        "rationale": "This describes a supervising broker, not an associate broker.",
        "isCorrect": false
      },
      {
        "text": "A person with no license",
        "rationale": "An associate broker holds a broker license.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a broker who chooses to work under supervision rather than independently."
  },
  {
    "questionNumber": 9,
    "question": "What is required for Texas license suspension?",
    "answerOptions": [
      {
        "text": "Only failure to pay renewal fees",
        "rationale": "Suspension can occur for various violations, not just non-payment.",
        "isCorrect": false
      },
      {
        "text": "Violations of real estate laws or regulations",
        "rationale": "License suspension can occur for violations of real estate laws, regulations, or ethical standards.",
        "isCorrect": true
      },
      {
        "text": "Only criminal convictions",
        "rationale": "Suspension can occur for various violations, not just criminal convictions.",
        "isCorrect": false
      },
      {
        "text": "Only complaints from consumers",
        "rationale": "Suspension requires investigation and determination of violations.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what actions would warrant temporary removal of license privileges."
  },
  {
    "questionNumber": 10,
    "question": "What is the purpose of Texas continuing education requirements?",
    "answerOptions": [
      {
        "text": "To increase revenue for TREC",
        "rationale": "Continuing education is for maintaining competency, not revenue generation.",
        "isCorrect": false
      },
      {
        "text": "To ensure licensees stay current with laws, regulations, and industry practices",
        "rationale": "Continuing education ensures licensees maintain competency and stay current with changes.",
        "isCorrect": true
      },
      {
        "text": "To limit the number of licensees",
        "rationale": "Continuing education is for competency, not limiting licensees.",
        "isCorrect": false
      },
      {
        "text": "To replace the initial licensing examination",
        "rationale": "Continuing education supplements, not replaces, initial education.",
        "isCorrect": false
      }
    ],
    "hint": "Consider the purpose of ongoing education in professional fields."
  },
  {
    "questionNumber": 11,
    "question": "What is a Texas branch office?",
    "answerOptions": [
      {
        "text": "A secondary office location operated by a licensed broker",
        "rationale": "A branch office is a secondary location operated by a licensed broker.",
        "isCorrect": true
      },
      {
        "text": "An office operated by a salesperson",
        "rationale": "Salespersons cannot operate offices; only brokers can.",
        "isCorrect": false
      },
      {
        "text": "A virtual office with no physical location",
        "rationale": "Branch offices must have physical locations and be registered with TREC.",
        "isCorrect": false
      },
      {
        "text": "An office in a different state",
        "rationale": "Branch offices are secondary locations within Texas.",
        "isCorrect": false
      }
    ],
    "hint": "Think about additional office locations beyond the main office."
  },
  {
    "questionNumber": 12,
    "question": "What is required for a Texas branch office?",
    "answerOptions": [
      {
        "text": "Only registration with TREC",
        "rationale": "Branch offices must be registered and supervised by a licensed broker.",
        "isCorrect": false
      },
      {
        "text": "Registration with TREC and supervision by a licensed broker",
        "rationale": "Branch offices must be registered with TREC and supervised by a licensed broker.",
        "isCorrect": true
      },
      {
        "text": "Only supervision by a salesperson",
        "rationale": "Salespersons cannot supervise offices; only brokers can.",
        "isCorrect": false
      },
      {
        "text": "No special requirements",
        "rationale": "Branch offices have specific registration and supervision requirements.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what oversight and registration would be needed for additional locations."
  },
  {
    "questionNumber": 13,
    "question": "What is a Texas trade name?",
    "answerOptions": [
      {
        "text": "A business name used by a broker that differs from their legal name",
        "rationale": "A trade name is a business name used by a broker that differs from their legal name.",
        "isCorrect": true
      },
      {
        "text": "The legal name of a brokerage company",
        "rationale": "A trade name is different from the legal name.",
        "isCorrect": false
      },
      {
        "text": "A name used only for advertising",
        "rationale": "Trade names must be registered and used consistently, not just for advertising.",
        "isCorrect": false
      },
      {
        "text": "A temporary name for new businesses",
        "rationale": "Trade names are permanent business names, not temporary ones.",
        "isCorrect": false
      }
    ],
    "hint": "Think about business names that differ from personal or legal names."
  },
  {
    "questionNumber": 14,
    "question": "What is required for using a Texas trade name?",
    "answerOptions": [
      {
        "text": "Only choosing a name",
        "rationale": "Trade names must be registered with TREC and cannot be misleading.",
        "isCorrect": false
      },
      {
        "text": "Registration with TREC and ensuring the name is not misleading",
        "rationale": "Trade names must be registered with TREC and cannot be misleading to the public.",
        "isCorrect": true
      },
      {
        "text": "Only ensuring the name is not misleading",
        "rationale": "Registration with TREC is also required.",
        "isCorrect": false
      },
      {
        "text": "No special requirements",
        "rationale": "Trade names have specific registration and approval requirements.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what would protect consumers from misleading business names."
  },
  {
    "questionNumber": 15,
    "question": "What is Texas license reciprocity?",
    "answerOptions": [
      {
        "text": "Recognition of licenses from other states for Texas licensing",
        "rationale": "Reciprocity allows recognition of licenses from other states for Texas licensing.",
        "isCorrect": true
      },
      {
        "text": "Recognition of Texas licenses in other states",
        "rationale": "This describes Texas licenses being recognized elsewhere, not reciprocity into Texas.",
        "isCorrect": false
      },
      {
        "text": "Automatic license transfer between states",
        "rationale": "Reciprocity requires application and approval, not automatic transfer.",
        "isCorrect": false
      },
      {
        "text": "Free license renewal for Texas licensees",
        "rationale": "Reciprocity is about recognizing other states' licenses, not free renewals.",
        "isCorrect": false
      }
    ],
    "hint": "Think about mutual recognition agreements between states."
  },
  {
    "questionNumber": 16,
    "question": "What is a Texas license endorsement?",
    "answerOptions": [
      {
        "text": "Adding additional qualifications to an existing license",
        "rationale": "A license endorsement adds additional qualifications to an existing license.",
        "isCorrect": true
      },
      {
        "text": "Renewing an existing license",
        "rationale": "Endorsement is different from renewal; it adds qualifications.",
        "isCorrect": false
      },
      {
        "text": "Transferring a license to another broker",
        "rationale": "Endorsement adds qualifications, it doesn't transfer licenses.",
        "isCorrect": false
      },
      {
        "text": "Suspending a license",
        "rationale": "Endorsement adds qualifications, it doesn't suspend licenses.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what would allow additional types of real estate activities."
  },
  {
    "questionNumber": 17,
    "question": "What is required for Texas license reinstatement?",
    "answerOptions": [
      {
        "text": "Only payment of reinstatement fees",
        "rationale": "Reinstatement requires meeting specific conditions and paying fees.",
        "isCorrect": false
      },
      {
        "text": "Meeting specific conditions and paying reinstatement fees",
        "rationale": "License reinstatement requires meeting specific conditions and paying reinstatement fees.",
        "isCorrect": true
      },
      {
        "text": "Only completing continuing education",
        "rationale": "Reinstatement requires meeting conditions and paying fees, not just education.",
        "isCorrect": false
      },
      {
        "text": "Only submitting an application",
        "rationale": "Reinstatement requires meeting conditions and paying fees, not just an application.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what would be required to restore a suspended or revoked license."
  },
  {
    "questionNumber": 18,
    "question": "What is a Texas license inactive status?",
    "answerOptions": [
      {
        "text": "A status where a license is not actively used but remains valid",
        "rationale": "Inactive status means the license is not actively used but remains valid.",
        "isCorrect": true
      },
      {
        "text": "A status where a license is suspended",
        "rationale": "Inactive status is different from suspension; the license remains valid.",
        "isCorrect": false
      },
      {
        "text": "A status where a license is revoked",
        "rationale": "Inactive status is different from revocation; the license remains valid.",
        "isCorrect": false
      },
      {
        "text": "A status where a license is expired",
        "rationale": "Inactive status is different from expiration; the license remains valid.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a license that's valid but not currently being used."
  },
  {
    "questionNumber": 19,
    "question": "What is a Texas license active status?",
    "answerOptions": [
      {
        "text": "A status where a license is actively used for real estate activities",
        "rationale": "Active status means the license is actively used for real estate activities.",
        "isCorrect": true
      },
      {
        "text": "A status where a license is suspended",
        "rationale": "Active status means the license can be used, not suspended.",
        "isCorrect": false
      },
      {
        "text": "A status where a license is expired",
        "rationale": "Active status means the license is current and can be used.",
        "isCorrect": false
      },
      {
        "text": "A status where a license is inactive",
        "rationale": "Active status is the opposite of inactive status.",
        "isCorrect": false
      }
    ],
    "hint": "Consider the opposite of inactive status."
  },
  {
    "questionNumber": 20,
    "question": "What is required for Texas license examination?",
    "answerOptions": [
      {
        "text": "Only passing the national portion",
        "rationale": "Both national and Texas state portions must be passed.",
        "isCorrect": false
      },
      {
        "text": "Passing both national and Texas state portions",
        "rationale": "Texas license examination requires passing both national and state-specific portions.",
        "isCorrect": true
      },
      {
        "text": "Only passing the Texas state portion",
        "rationale": "Both national and state portions must be passed.",
        "isCorrect": false
      },
      {
        "text": "Only completing pre-license education",
        "rationale": "Education is required before the examination, but both exam portions must be passed.",
        "isCorrect": false
      }
    ],
    "hint": "Consider that Texas requires both general real estate knowledge and state-specific knowledge."
  },

  // BATCH 2: Texas Standards of Conduct (Questions 21-40)
  {
    "questionNumber": 21,
    "question": "What is fiduciary duty in Texas real estate?",
    "answerOptions": [
      {
        "text": "The duty to charge reasonable fees",
        "rationale": "Fiduciary duty involves the highest standard of care, not just reasonable fees.",
        "isCorrect": false
      },
      {
        "text": "The highest standard of care owed by a licensee to their client",
        "rationale": "Fiduciary duty is the highest standard of care owed by a licensee to their client.",
        "isCorrect": true
      },
      {
        "text": "The duty to advertise properties",
        "rationale": "Fiduciary duty involves the highest standard of care, not just advertising.",
        "isCorrect": false
      },
      {
        "text": "The duty to complete transactions quickly",
        "rationale": "Fiduciary duty involves the highest standard of care, not speed of transactions.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the highest level of trust and care in professional relationships."
  },
  {
    "questionNumber": 22,
    "question": "What are the five fiduciary duties in Texas real estate?",
    "answerOptions": [
      {
        "text": "Loyalty, confidentiality, disclosure, obedience, and accounting",
        "rationale": "The five fiduciary duties are loyalty, confidentiality, disclosure, obedience, and accounting.",
        "isCorrect": true
      },
      {
        "text": "Honesty, integrity, fairness, competence, and diligence",
        "rationale": "These are general ethical principles, not the specific fiduciary duties.",
        "isCorrect": false
      },
      {
        "text": "Communication, negotiation, marketing, closing, and follow-up",
        "rationale": "These are activities, not fiduciary duties.",
        "isCorrect": false
      },
      {
        "text": "Advertising, prospecting, showing, negotiating, and closing",
        "rationale": "These are activities, not fiduciary duties.",
        "isCorrect": false
      }
    ],
    "hint": "Remember the acronym L-C-D-O-A for the five fiduciary duties."
  },
  {
    "questionNumber": 23,
    "question": "What is the duty of loyalty in Texas real estate?",
    "answerOptions": [
      {
        "text": "The duty to act in the client's best interest above all others",
        "rationale": "Loyalty requires acting in the client's best interest above all others.",
        "isCorrect": true
      },
      {
        "text": "The duty to be honest with all parties",
        "rationale": "This describes general honesty, not the specific duty of loyalty.",
        "isCorrect": false
      },
      {
        "text": "The duty to complete transactions quickly",
        "rationale": "Loyalty is about acting in the client's best interest, not speed.",
        "isCorrect": false
      },
      {
        "text": "The duty to charge reasonable fees",
        "rationale": "Loyalty is about acting in the client's best interest, not fees.",
        "isCorrect": false
      }
    ],
    "hint": "Think about putting the client's interests first."
  },
  {
    "questionNumber": 24,
    "question": "What is the duty of confidentiality in Texas real estate?",
    "answerOptions": [
      {
        "text": "The duty to keep client information private and not disclose it",
        "rationale": "Confidentiality requires keeping client information private and not disclosing it.",
        "isCorrect": true
      },
      {
        "text": "The duty to share information with other agents",
        "rationale": "Confidentiality requires keeping information private, not sharing it.",
        "isCorrect": false
      },
      {
        "text": "The duty to advertise client properties",
        "rationale": "Confidentiality is about keeping information private, not advertising.",
        "isCorrect": false
      },
      {
        "text": "The duty to complete transactions quickly",
        "rationale": "Confidentiality is about keeping information private, not speed.",
        "isCorrect": false
      }
    ],
    "hint": "Think about keeping sensitive client information private."
  },
  {
    "questionNumber": 25,
    "question": "What is the duty of disclosure in Texas real estate?",
    "answerOptions": [
      {
        "text": "The duty to reveal all material facts about a property or transaction",
        "rationale": "Disclosure requires revealing all material facts about a property or transaction.",
        "isCorrect": true
      },
      {
        "text": "The duty to keep client information confidential",
        "rationale": "This describes confidentiality, not disclosure.",
        "isCorrect": false
      },
      {
        "text": "The duty to charge reasonable fees",
        "rationale": "Disclosure is about revealing material facts, not fees.",
        "isCorrect": false
      },
      {
        "text": "The duty to complete transactions quickly",
        "rationale": "Disclosure is about revealing material facts, not speed.",
        "isCorrect": false
      }
    ],
    "hint": "Think about revealing important information that could affect decisions."
  },
  {
    "questionNumber": 26,
    "question": "What is the duty of obedience in Texas real estate?",
    "answerOptions": [
      {
        "text": "The duty to follow lawful instructions from the client",
        "rationale": "Obedience requires following lawful instructions from the client.",
        "isCorrect": true
      },
      {
        "text": "The duty to follow all instructions regardless of legality",
        "rationale": "Obedience only applies to lawful instructions, not illegal ones.",
        "isCorrect": false
      },
      {
        "text": "The duty to follow broker instructions only",
        "rationale": "Obedience applies to client instructions, not just broker instructions.",
        "isCorrect": false
      },
      {
        "text": "The duty to follow market trends",
        "rationale": "Obedience is about following client instructions, not market trends.",
        "isCorrect": false
      }
    ],
    "hint": "Think about following client directions, but only when they're legal."
  },
  {
    "questionNumber": 27,
    "question": "What is the duty of accounting in Texas real estate?",
    "answerOptions": [
      {
        "text": "The duty to account for all money and property received",
        "rationale": "Accounting requires accounting for all money and property received.",
        "isCorrect": true
      },
      {
        "text": "The duty to charge reasonable fees",
        "rationale": "Accounting is about tracking money and property, not setting fees.",
        "isCorrect": false
      },
      {
        "text": "The duty to complete transactions quickly",
        "rationale": "Accounting is about tracking money and property, not speed.",
        "isCorrect": false
      },
      {
        "text": "The duty to advertise properties",
        "rationale": "Accounting is about tracking money and property, not advertising.",
        "isCorrect": false
      }
    ],
    "hint": "Think about keeping accurate records of money and property."
  },
  {
    "questionNumber": 28,
    "question": "What is a material fact in Texas real estate?",
    "answerOptions": [
      {
        "text": "Information that could influence a reasonable person's decision",
        "rationale": "A material fact is information that could influence a reasonable person's decision.",
        "isCorrect": true
      },
      {
        "text": "Any information about a property",
        "rationale": "Not all information is material; only information that could influence decisions.",
        "isCorrect": false
      },
      {
        "text": "Only information about property defects",
        "rationale": "Material facts include more than just defects; any influential information.",
        "isCorrect": false
      },
      {
        "text": "Only information about property value",
        "rationale": "Material facts include more than just value; any influential information.",
        "isCorrect": false
      }
    ],
    "hint": "Think about information that would matter to a reasonable buyer or seller."
  },
  {
    "questionNumber": 29,
    "question": "What is dual agency in Texas?",
    "answerOptions": [
      {
        "text": "Representing both buyer and seller in the same transaction",
        "rationale": "Dual agency occurs when representing both buyer and seller in the same transaction.",
        "isCorrect": true
      },
      {
        "text": "Representing two different buyers",
        "rationale": "This describes representing multiple buyers, not dual agency.",
        "isCorrect": false
      },
      {
        "text": "Representing two different sellers",
        "rationale": "This describes representing multiple sellers, not dual agency.",
        "isCorrect": false
      },
      {
        "text": "Working with two different brokers",
        "rationale": "This describes working with multiple brokers, not dual agency.",
        "isCorrect": false
      }
    ],
    "hint": "Think about representing both sides of a transaction."
  },
  {
    "questionNumber": 30,
    "question": "What is required for dual agency in Texas?",
    "answerOptions": [
      {
        "text": "Written consent from both parties and limits on representation",
        "rationale": "Dual agency requires written consent from both parties and limits on representation.",
        "isCorrect": true
      },
      {
        "text": "Only verbal consent from both parties",
        "rationale": "Written consent is required, not just verbal consent.",
        "isCorrect": false
      },
      {
        "text": "Only consent from the buyer",
        "rationale": "Consent from both parties is required.",
        "isCorrect": false
      },
      {
        "text": "No special requirements",
        "rationale": "Dual agency has specific consent and limitation requirements.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what would protect both parties when one agent represents both."
  },
  {
    "questionNumber": 31,
    "question": "What is designated agency in Texas?",
    "answerOptions": [
      {
        "text": "When different licensees in the same firm represent different parties",
        "rationale": "Designated agency occurs when different licensees in the same firm represent different parties.",
        "isCorrect": true
      },
      {
        "text": "When one licensee represents both parties",
        "rationale": "This describes dual agency, not designated agency.",
        "isCorrect": false
      },
      {
        "text": "When a licensee works with multiple brokers",
        "rationale": "This describes working with multiple brokers, not designated agency.",
        "isCorrect": false
      },
      {
        "text": "When a licensee represents only one party",
        "rationale": "Designated agency involves multiple parties represented by different licensees.",
        "isCorrect": false
      }
    ],
    "hint": "Think about different agents in the same company representing different sides."
  },
  {
    "questionNumber": 32,
    "question": "What is subagency in Texas?",
    "answerOptions": [
      {
        "text": "When a licensee represents another licensee's client",
        "rationale": "Subagency occurs when a licensee represents another licensee's client.",
        "isCorrect": true
      },
      {
        "text": "When a licensee works under another broker",
        "rationale": "This describes working under supervision, not subagency.",
        "isCorrect": false
      },
      {
        "text": "When a licensee represents multiple clients",
        "rationale": "This describes representing multiple clients, not subagency.",
        "isCorrect": false
      },
      {
        "text": "When a licensee works independently",
        "rationale": "Subagency involves representing another licensee's client, not independence.",
        "isCorrect": false
      }
    ],
    "hint": "Think about representing someone else's client."
  },
  {
    "questionNumber": 33,
    "question": "What is customer service in Texas real estate?",
    "answerOptions": [
      {
        "text": "Providing assistance without creating agency relationship",
        "rationale": "Customer service involves providing assistance without creating an agency relationship.",
        "isCorrect": true
      },
      {
        "text": "Creating an agency relationship with all parties",
        "rationale": "Customer service avoids creating agency relationships, not creating them.",
        "isCorrect": false
      },
      {
        "text": "Only providing information about properties",
        "rationale": "Customer service involves more than just property information.",
        "isCorrect": false
      },
      {
        "text": "Only showing properties to buyers",
        "rationale": "Customer service involves more than just showing properties.",
        "isCorrect": false
      }
    ],
    "hint": "Think about helping without creating a fiduciary relationship."
  },
  {
    "questionNumber": 34,
    "question": "What is fair housing in Texas?",
    "answerOptions": [
      {
        "text": "Federal and state laws prohibiting discrimination in housing",
        "rationale": "Fair housing laws prohibit discrimination in housing based on protected characteristics.",
        "isCorrect": true
      },
      {
        "text": "Laws requiring equal commission rates",
        "rationale": "Fair housing is about discrimination, not commission rates.",
        "isCorrect": false
      },
      {
        "text": "Laws requiring equal property values",
        "rationale": "Fair housing is about discrimination, not property values.",
        "isCorrect": false
      },
      {
        "text": "Laws requiring equal advertising",
        "rationale": "Fair housing is about discrimination, not advertising.",
        "isCorrect": false
      }
    ],
    "hint": "Think about laws that prevent discrimination in housing."
  },
  {
    "questionNumber": 35,
    "question": "What is steering in Texas real estate?",
    "answerOptions": [
      {
        "text": "Directing clients to or away from certain areas based on protected characteristics",
        "rationale": "Steering involves directing clients based on protected characteristics, which is illegal.",
        "isCorrect": true
      },
      {
        "text": "Directing clients to properties that meet their criteria",
        "rationale": "This is legitimate assistance, not illegal steering.",
        "isCorrect": false
      },
      {
        "text": "Directing clients to properties with good schools",
        "rationale": "This is legitimate assistance based on client preferences, not illegal steering.",
        "isCorrect": false
      },
      {
        "text": "Directing clients to properties within their budget",
        "rationale": "This is legitimate assistance based on financial criteria, not illegal steering.",
        "isCorrect": false
      }
    ],
    "hint": "Think about illegal discrimination based on protected characteristics."
  },
  {
    "questionNumber": 36,
    "question": "What is blockbusting in Texas?",
    "answerOptions": [
      {
        "text": "Encouraging property owners to sell due to changing neighborhood demographics",
        "rationale": "Blockbusting involves encouraging sales based on changing demographics, which is illegal.",
        "isCorrect": true
      },
      {
        "text": "Encouraging property owners to sell due to market conditions",
        "rationale": "This is legitimate market advice, not illegal blockbusting.",
        "isCorrect": false
      },
      {
        "text": "Encouraging property owners to sell due to personal circumstances",
        "rationale": "This is legitimate assistance, not illegal blockbusting.",
        "isCorrect": false
      },
      {
        "text": "Encouraging property owners to sell due to property condition",
        "rationale": "This is legitimate advice, not illegal blockbusting.",
        "isCorrect": false
      }
    ],
    "hint": "Think about illegal practices that exploit demographic changes."
  },
  {
    "questionNumber": 37,
    "question": "What is redlining in Texas?",
    "answerOptions": [
      {
        "text": "Refusing to provide services based on neighborhood demographics",
        "rationale": "Redlining involves refusing services based on neighborhood demographics, which is illegal.",
        "isCorrect": true
      },
      {
        "text": "Refusing to provide services based on property condition",
        "rationale": "This is legitimate business practice, not illegal redlining.",
        "isCorrect": false
      },
      {
        "text": "Refusing to provide services based on client credit",
        "rationale": "This is legitimate business practice, not illegal redlining.",
        "isCorrect": false
      },
      {
        "text": "Refusing to provide services based on property value",
        "rationale": "This is legitimate business practice, not illegal redlining.",
        "isCorrect": false
      }
    ],
    "hint": "Think about illegal discrimination based on neighborhood characteristics."
  },
  {
    "questionNumber": 38,
    "question": "What is reasonable accommodation in Texas?",
    "answerOptions": [
      {
        "text": "Changes to rules or practices to accommodate persons with disabilities",
        "rationale": "Reasonable accommodation involves changes to rules or practices for persons with disabilities.",
        "isCorrect": true
      },
      {
        "text": "Changes to property to accommodate persons with disabilities",
        "rationale": "This describes reasonable modification, not reasonable accommodation.",
        "isCorrect": false
      },
      {
        "text": "Changes to commission rates for persons with disabilities",
        "rationale": "Reasonable accommodation is about rules and practices, not commission rates.",
        "isCorrect": false
      },
      {
        "text": "Changes to advertising for persons with disabilities",
        "rationale": "Reasonable accommodation is about rules and practices, not advertising.",
        "isCorrect": false
      }
    ],
    "hint": "Think about changes to rules or practices, not physical changes."
  },
  {
    "questionNumber": 39,
    "question": "What is reasonable modification in Texas?",
    "answerOptions": [
      {
        "text": "Physical changes to property to accommodate persons with disabilities",
        "rationale": "Reasonable modification involves physical changes to property for persons with disabilities.",
        "isCorrect": true
      },
      {
        "text": "Changes to rules or practices to accommodate persons with disabilities",
        "rationale": "This describes reasonable accommodation, not reasonable modification.",
        "isCorrect": false
      },
      {
        "text": "Changes to commission rates for persons with disabilities",
        "rationale": "Reasonable modification is about physical changes, not commission rates.",
        "isCorrect": false
      },
      {
        "text": "Changes to advertising for persons with disabilities",
        "rationale": "Reasonable modification is about physical changes, not advertising.",
        "isCorrect": false
      }
    ],
    "hint": "Think about physical changes to property, not changes to rules."
  },
  {
    "questionNumber": 40,
    "question": "What is sexual harassment in Texas real estate?",
    "answerOptions": [
      {
        "text": "Unwelcome sexual advances or conduct in housing transactions",
        "rationale": "Sexual harassment involves unwelcome sexual advances or conduct in housing transactions.",
        "isCorrect": true
      },
      {
        "text": "Professional relationships between licensees",
        "rationale": "Professional relationships are not sexual harassment unless they involve unwelcome advances.",
        "isCorrect": false
      },
      {
        "text": "Friendly interactions between licensees and clients",
        "rationale": "Friendly interactions are not sexual harassment unless they involve unwelcome advances.",
        "isCorrect": false
      },
      {
        "text": "Business meetings between licensees and clients",
        "rationale": "Business meetings are not sexual harassment unless they involve unwelcome advances.",
        "isCorrect": false
      }
    ],
    "hint": "Think about unwelcome sexual behavior in professional contexts."
  },

  // BATCH 3: Texas Contracts (Questions 41-60)
  {
    "questionNumber": 41,
    "question": "What is required for a valid real estate contract in Texas?",
    "answerOptions": [
      {
        "text": "Offer, acceptance, consideration, and legal capacity",
        "rationale": "Valid contracts require offer, acceptance, consideration, and legal capacity.",
        "isCorrect": true
      },
      {
        "text": "Only offer and acceptance",
        "rationale": "Contracts also require consideration and legal capacity.",
        "isCorrect": false
      },
      {
        "text": "Only written agreement",
        "rationale": "While real estate contracts must be written, they also need the basic elements.",
        "isCorrect": false
      },
      {
        "text": "Only notarization",
        "rationale": "Notarization is not required for contract validity, though it may be required for recording.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the basic elements that make any contract legally binding."
  },
  {
    "questionNumber": 42,
    "question": "What is the Statute of Frauds in Texas?",
    "answerOptions": [
      {
        "text": "Requirement that certain contracts must be in writing to be enforceable",
        "rationale": "The Statute of Frauds requires certain contracts, including real estate contracts, to be in writing.",
        "isCorrect": true
      },
      {
        "text": "Requirement that all contracts must be notarized",
        "rationale": "The Statute of Frauds is about writing requirements, not notarization.",
        "isCorrect": false
      },
      {
        "text": "Requirement that contracts must be recorded",
        "rationale": "The Statute of Frauds is about writing requirements, not recording.",
        "isCorrect": false
      },
      {
        "text": "Requirement that contracts must be witnessed",
        "rationale": "The Statute of Frauds is about writing requirements, not witnessing.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the legal requirement for written contracts in certain situations."
  },
  {
    "questionNumber": 43,
    "question": "What is earnest money in Texas real estate?",
    "answerOptions": [
      {
        "text": "Money deposited to show good faith in a real estate transaction",
        "rationale": "Earnest money is deposited to show good faith and commitment to the transaction.",
        "isCorrect": true
      },
      {
        "text": "Money paid to the real estate agent as commission",
        "rationale": "Earnest money is not commission; it's a good faith deposit.",
        "isCorrect": false
      },
      {
        "text": "Money paid to the lender for loan processing",
        "rationale": "Earnest money is not loan processing fees; it's a good faith deposit.",
        "isCorrect": false
      },
      {
        "text": "Money paid for property inspection",
        "rationale": "Earnest money is not inspection fees; it's a good faith deposit.",
        "isCorrect": false
      }
    ],
    "hint": "Think about money that demonstrates commitment to completing a transaction."
  },
  {
    "questionNumber": 44,
    "question": "What happens to earnest money if a buyer defaults in Texas?",
    "answerOptions": [
      {
        "text": "The seller may be entitled to keep the earnest money",
        "rationale": "If a buyer defaults, the seller may be entitled to keep the earnest money as liquidated damages.",
        "isCorrect": true
      },
      {
        "text": "The earnest money must be returned to the buyer",
        "rationale": "If the buyer defaults, the seller may keep the earnest money, not return it.",
        "isCorrect": false
      },
      {
        "text": "The earnest money goes to the real estate agent",
        "rationale": "Earnest money goes to the seller, not the agent, if the buyer defaults.",
        "isCorrect": false
      },
      {
        "text": "The earnest money goes to the state",
        "rationale": "Earnest money goes to the seller, not the state, if the buyer defaults.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what happens when someone breaks their commitment to a contract."
  },
  {
    "questionNumber": 45,
    "question": "What is a Texas Real Estate Commission (TREC) contract form?",
    "answerOptions": [
      {
        "text": "A standardized contract form approved by TREC for real estate transactions",
        "rationale": "TREC contract forms are standardized forms approved by TREC for real estate transactions.",
        "isCorrect": true
      },
      {
        "text": "A contract form created by individual brokers",
        "rationale": "TREC forms are standardized, not created by individual brokers.",
        "isCorrect": false
      },
      {
        "text": "A contract form required by federal law",
        "rationale": "TREC forms are state-specific, not federal requirements.",
        "isCorrect": false
      },
      {
        "text": "A contract form used only for commercial properties",
        "rationale": "TREC forms are used for various property types, not just commercial.",
        "isCorrect": false
      }
    ],
    "hint": "Think about standardized forms that provide consistency and legal protection."
  },
  {
    "questionNumber": 46,
    "question": "What is a counteroffer in Texas real estate?",
    "answerOptions": [
      {
        "text": "A rejection of the original offer with a new offer",
        "rationale": "A counteroffer rejects the original offer and makes a new offer.",
        "isCorrect": true
      },
      {
        "text": "An acceptance of the original offer",
        "rationale": "A counteroffer is not an acceptance; it's a rejection with a new offer.",
        "isCorrect": false
      },
      {
        "text": "A request for more information",
        "rationale": "A counteroffer is not a request for information; it's a new offer.",
        "isCorrect": false
      },
      {
        "text": "A withdrawal of the original offer",
        "rationale": "A counteroffer is not a withdrawal; it's a rejection with a new offer.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what happens when someone doesn't accept an offer but makes a new one."
  },
  {
    "questionNumber": 47,
    "question": "What is the difference between an option and a contract in Texas?",
    "answerOptions": [
      {
        "text": "An option gives the right to buy; a contract creates an obligation to buy",
        "rationale": "An option gives the right to buy without obligation; a contract creates an obligation to buy.",
        "isCorrect": true
      },
      {
        "text": "There is no difference between an option and a contract",
        "rationale": "Options and contracts have different legal effects.",
        "isCorrect": false
      },
      {
        "text": "An option is only for commercial properties",
        "rationale": "Options can be used for any property type, not just commercial.",
        "isCorrect": false
      },
      {
        "text": "A contract is only for residential properties",
        "rationale": "Contracts can be used for any property type, not just residential.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the difference between having a right versus having an obligation."
  },
  {
    "questionNumber": 48,
    "question": "What is a lease option in Texas?",
    "answerOptions": [
      {
        "text": "A lease that includes an option to purchase the property",
        "rationale": "A lease option combines a lease with an option to purchase the property.",
        "isCorrect": true
      },
      {
        "text": "A lease that can be renewed",
        "rationale": "This describes a renewable lease, not a lease option.",
        "isCorrect": false
      },
      {
        "text": "A lease with no security deposit",
        "rationale": "A lease option may still require a security deposit.",
        "isCorrect": false
      },
      {
        "text": "A lease for commercial properties only",
        "rationale": "Lease options can be used for any property type, not just commercial.",
        "isCorrect": false
      }
    ],
    "hint": "Think about combining rental and purchase rights in one agreement."
  },
  {
    "questionNumber": 49,
    "question": "What is a land contract in Texas?",
    "answerOptions": [
      {
        "text": "A contract where the seller finances the purchase",
        "rationale": "A land contract involves seller financing where the seller acts as the lender.",
        "isCorrect": true
      },
      {
        "text": "A contract for vacant land only",
        "rationale": "Land contracts can be used for any property type, not just vacant land.",
        "isCorrect": false
      },
      {
        "text": "A contract with no down payment",
        "rationale": "Land contracts may still require down payments.",
        "isCorrect": false
      },
      {
        "text": "A contract for commercial properties only",
        "rationale": "Land contracts can be used for any property type, not just commercial.",
        "isCorrect": false
      }
    ],
    "hint": "Think about seller financing arrangements."
  },
  {
    "questionNumber": 50,
    "question": "What is a contract for deed in Texas?",
    "answerOptions": [
      {
        "text": "A contract where the buyer makes payments but doesn't receive title until paid in full",
        "rationale": "A contract for deed involves payments without title transfer until the contract is completed.",
        "isCorrect": true
      },
      {
        "text": "A contract where the buyer receives title immediately",
        "rationale": "In a contract for deed, title is not transferred until the contract is completed.",
        "isCorrect": false
      },
      {
        "text": "A contract for vacant land only",
        "rationale": "Contracts for deed can be used for any property type, not just vacant land.",
        "isCorrect": false
      },
      {
        "text": "A contract with no interest charges",
        "rationale": "Contracts for deed may include interest charges.",
        "isCorrect": false
      }
    ],
    "hint": "Think about installment sales where title transfer is delayed."
  },
  {
    "questionNumber": 51,
    "question": "What is a contingency in a Texas real estate contract?",
    "answerOptions": [
      {
        "text": "A condition that must be met for the contract to be binding",
        "rationale": "A contingency is a condition that must be met for the contract to be binding.",
        "isCorrect": true
      },
      {
        "text": "A requirement that the contract be in writing",
        "rationale": "This describes the Statute of Frauds, not a contingency.",
        "isCorrect": false
      },
      {
        "text": "A requirement that the contract be notarized",
        "rationale": "This describes notarization, not a contingency.",
        "isCorrect": false
      },
      {
        "text": "A requirement that the contract be recorded",
        "rationale": "This describes recording, not a contingency.",
        "isCorrect": false
      }
    ],
    "hint": "Think about conditions that must be satisfied for a contract to be enforceable."
  },
  {
    "questionNumber": 52,
    "question": "What is a financing contingency in Texas?",
    "answerOptions": [
      {
        "text": "A condition that the buyer must obtain financing for the contract to be binding",
        "rationale": "A financing contingency requires the buyer to obtain financing for the contract to be binding.",
        "isCorrect": true
      },
      {
        "text": "A condition that the seller must provide financing",
        "rationale": "A financing contingency is about buyer financing, not seller financing.",
        "isCorrect": false
      },
      {
        "text": "A condition that the property must be appraised",
        "rationale": "This describes an appraisal contingency, not a financing contingency.",
        "isCorrect": false
      },
      {
        "text": "A condition that the property must be inspected",
        "rationale": "This describes an inspection contingency, not a financing contingency.",
        "isCorrect": false
      }
    ],
    "hint": "Think about conditions related to obtaining a loan."
  },
  {
    "questionNumber": 53,
    "question": "What is an inspection contingency in Texas?",
    "answerOptions": [
      {
        "text": "A condition that the property must pass inspection for the contract to be binding",
        "rationale": "An inspection contingency requires the property to pass inspection for the contract to be binding.",
        "isCorrect": true
      },
      {
        "text": "A condition that the buyer must be inspected",
        "rationale": "An inspection contingency is about property inspection, not buyer inspection.",
        "isCorrect": false
      },
      {
        "text": "A condition that the seller must be inspected",
        "rationale": "An inspection contingency is about property inspection, not seller inspection.",
        "isCorrect": false
      },
      {
        "text": "A condition that the agent must be inspected",
        "rationale": "An inspection contingency is about property inspection, not agent inspection.",
        "isCorrect": false
      }
    ],
    "hint": "Think about conditions related to property condition assessment."
  },
  {
    "questionNumber": 54,
    "question": "What is a title contingency in Texas?",
    "answerOptions": [
      {
        "text": "A condition that the property must have clear title for the contract to be binding",
        "rationale": "A title contingency requires the property to have clear title for the contract to be binding.",
        "isCorrect": true
      },
      {
        "text": "A condition that the buyer must have clear title",
        "rationale": "A title contingency is about property title, not buyer title.",
        "isCorrect": false
      },
      {
        "text": "A condition that the seller must have clear title",
        "rationale": "A title contingency is about property title, not seller title.",
        "isCorrect": false
      },
      {
        "text": "A condition that the agent must have clear title",
        "rationale": "A title contingency is about property title, not agent title.",
        "isCorrect": false
      }
    ],
    "hint": "Think about conditions related to property ownership and liens."
  },
  {
    "questionNumber": 55,
    "question": "What is a closing date in Texas?",
    "answerOptions": [
      {
        "text": "The date when the real estate transaction is completed",
        "rationale": "The closing date is when the real estate transaction is completed and ownership transfers.",
        "isCorrect": true
      },
      {
        "text": "The date when the contract is signed",
        "rationale": "This describes the contract date, not the closing date.",
        "isCorrect": false
      },
      {
        "text": "The date when the property is listed",
        "rationale": "This describes the listing date, not the closing date.",
        "isCorrect": false
      },
      {
        "text": "The date when the property is shown",
        "rationale": "This describes the showing date, not the closing date.",
        "isCorrect": false
      }
    ],
    "hint": "Think about when the transaction is finalized and ownership changes hands."
  },
  {
    "questionNumber": 56,
    "question": "What is a closing statement in Texas?",
    "answerOptions": [
      {
        "text": "A document showing all costs and credits for the real estate transaction",
        "rationale": "A closing statement shows all costs and credits for the real estate transaction.",
        "isCorrect": true
      },
      {
        "text": "A document showing only the purchase price",
        "rationale": "A closing statement shows all costs and credits, not just the purchase price.",
        "isCorrect": false
      },
      {
        "text": "A document showing only the commission",
        "rationale": "A closing statement shows all costs and credits, not just the commission.",
        "isCorrect": false
      },
      {
        "text": "A document showing only the loan amount",
        "rationale": "A closing statement shows all costs and credits, not just the loan amount.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a comprehensive financial summary of the transaction."
  },
  {
    "questionNumber": 57,
    "question": "What is a deed in Texas?",
    "answerOptions": [
      {
        "text": "A document that transfers ownership of real property",
        "rationale": "A deed is a document that transfers ownership of real property.",
        "isCorrect": true
      },
      {
        "text": "A document that creates a lien on real property",
        "rationale": "This describes a mortgage or deed of trust, not a deed.",
        "isCorrect": false
      },
      {
        "text": "A document that creates a lease on real property",
        "rationale": "This describes a lease agreement, not a deed.",
        "isCorrect": false
      },
      {
        "text": "A document that creates an easement on real property",
        "rationale": "This describes an easement agreement, not a deed.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the document that transfers ownership rights."
  },
  {
    "questionNumber": 58,
    "question": "What is a warranty deed in Texas?",
    "answerOptions": [
      {
        "text": "A deed that provides the highest level of protection to the buyer",
        "rationale": "A warranty deed provides the highest level of protection to the buyer with full warranties.",
        "isCorrect": true
      },
      {
        "text": "A deed that provides no protection to the buyer",
        "rationale": "A warranty deed provides the highest level of protection, not no protection.",
        "isCorrect": false
      },
      {
        "text": "A deed that provides limited protection to the buyer",
        "rationale": "A warranty deed provides the highest level of protection, not limited protection.",
        "isCorrect": false
      },
      {
        "text": "A deed that provides protection only to the seller",
        "rationale": "A warranty deed provides protection to the buyer, not the seller.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the type of deed that offers the most protection to the buyer."
  },
  {
    "questionNumber": 59,
    "question": "What is a quitclaim deed in Texas?",
    "answerOptions": [
      {
        "text": "A deed that transfers whatever interest the grantor has without warranties",
        "rationale": "A quitclaim deed transfers whatever interest the grantor has without any warranties.",
        "isCorrect": true
      },
      {
        "text": "A deed that provides full warranties to the buyer",
        "rationale": "A quitclaim deed provides no warranties, not full warranties.",
        "isCorrect": false
      },
      {
        "text": "A deed that provides limited warranties to the buyer",
        "rationale": "A quitclaim deed provides no warranties, not limited warranties.",
        "isCorrect": false
      },
      {
        "text": "A deed that creates a lien on the property",
        "rationale": "A quitclaim deed transfers ownership, it doesn't create liens.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a deed that transfers interest without making any promises about the title."
  },
  {
    "questionNumber": 60,
    "question": "What is recording in Texas real estate?",
    "answerOptions": [
      {
        "text": "Filing documents with the county clerk to provide public notice",
        "rationale": "Recording involves filing documents with the county clerk to provide public notice.",
        "isCorrect": true
      },
      {
        "text": "Filing documents with TREC",
        "rationale": "Recording is done with the county clerk, not TREC.",
        "isCorrect": false
      },
      {
        "text": "Filing documents with the state",
        "rationale": "Recording is done with the county clerk, not the state.",
        "isCorrect": false
      },
      {
        "text": "Filing documents with the federal government",
        "rationale": "Recording is done with the county clerk, not the federal government.",
        "isCorrect": false
      }
    ],
    "hint": "Think about where documents are filed to give notice to the public."
  },

  // BATCH 4: Texas Agency/Brokerage (Questions 61-80)
  {
    "questionNumber": 61,
    "question": "What is agency in Texas real estate?",
    "answerOptions": [
      {
        "text": "A fiduciary relationship between a licensee and their client",
        "rationale": "Agency is a fiduciary relationship where the licensee represents the client's interests.",
        "isCorrect": true
      },
      {
        "text": "A business relationship between licensees",
        "rationale": "Agency is between a licensee and their client, not between licensees.",
        "isCorrect": false
      },
      {
        "text": "A relationship between buyers and sellers",
        "rationale": "Agency is between a licensee and their client, not between buyers and sellers.",
        "isCorrect": false
      },
      {
        "text": "A relationship between brokers and lenders",
        "rationale": "Agency is between a licensee and their client, not between brokers and lenders.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the professional relationship where someone represents another's interests."
  },
  {
    "questionNumber": 62,
    "question": "What is a principal in Texas real estate?",
    "answerOptions": [
      {
        "text": "The person who hires a licensee to represent them",
        "rationale": "A principal is the person who hires a licensee to represent them in a real estate transaction.",
        "isCorrect": true
      },
      {
        "text": "The licensee who represents a client",
        "rationale": "This describes the agent, not the principal.",
        "isCorrect": false
      },
      {
        "text": "The broker who supervises licensees",
        "rationale": "This describes a supervising broker, not a principal.",
        "isCorrect": false
      },
      {
        "text": "The person who owns the property",
        "rationale": "A principal is the person who hires representation, not necessarily the property owner.",
        "isCorrect": false
      }
    ],
    "hint": "Think about who hires someone to represent their interests."
  },
  {
    "questionNumber": 63,
    "question": "What is an agent in Texas real estate?",
    "answerOptions": [
      {
        "text": "The licensee who represents a principal",
        "rationale": "An agent is the licensee who represents a principal in a real estate transaction.",
        "isCorrect": true
      },
      {
        "text": "The person who hires a licensee",
        "rationale": "This describes the principal, not the agent.",
        "isCorrect": false
      },
      {
        "text": "The broker who supervises licensees",
        "rationale": "This describes a supervising broker, not an agent.",
        "isCorrect": false
      },
      {
        "text": "The person who owns the property",
        "rationale": "An agent is the licensee who represents someone, not the property owner.",
        "isCorrect": false
      }
    ],
    "hint": "Think about who represents someone else's interests."
  },
  {
    "questionNumber": 64,
    "question": "What is express agency in Texas?",
    "answerOptions": [
      {
        "text": "Agency created by written or verbal agreement",
        "rationale": "Express agency is created by explicit written or verbal agreement between the parties.",
        "isCorrect": true
      },
      {
        "text": "Agency created by implication",
        "rationale": "This describes implied agency, not express agency.",
        "isCorrect": false
      },
      {
        "text": "Agency created by law",
        "rationale": "This describes agency by estoppel, not express agency.",
        "isCorrect": false
      },
      {
        "text": "Agency created by necessity",
        "rationale": "This describes agency by necessity, not express agency.",
        "isCorrect": false
      }
    ],
    "hint": "Think about agency that is explicitly stated or written."
  },
  {
    "questionNumber": 65,
    "question": "What is implied agency in Texas?",
    "answerOptions": [
      {
        "text": "Agency created by the actions and conduct of the parties",
        "rationale": "Implied agency is created by the actions and conduct of the parties, not by explicit agreement.",
        "isCorrect": true
      },
      {
        "text": "Agency created by written agreement",
        "rationale": "This describes express agency, not implied agency.",
        "isCorrect": false
      },
      {
        "text": "Agency created by verbal agreement",
        "rationale": "This describes express agency, not implied agency.",
        "isCorrect": false
      },
      {
        "text": "Agency created by law",
        "rationale": "This describes agency by estoppel, not implied agency.",
        "isCorrect": false
      }
    ],
    "hint": "Think about agency that is created by behavior rather than explicit agreement."
  },
  {
    "questionNumber": 66,
    "question": "What is agency by estoppel in Texas?",
    "answerOptions": [
      {
        "text": "Agency created when a principal allows others to believe an agency relationship exists",
        "rationale": "Agency by estoppel occurs when a principal allows others to believe an agency relationship exists.",
        "isCorrect": true
      },
      {
        "text": "Agency created by written agreement",
        "rationale": "This describes express agency, not agency by estoppel.",
        "isCorrect": false
      },
      {
        "text": "Agency created by verbal agreement",
        "rationale": "This describes express agency, not agency by estoppel.",
        "isCorrect": false
      },
      {
        "text": "Agency created by the actions of the parties",
        "rationale": "This describes implied agency, not agency by estoppel.",
        "isCorrect": false
      }
    ],
    "hint": "Think about agency created by allowing others to believe a relationship exists."
  },
  {
    "questionNumber": 67,
    "question": "What is a listing agreement in Texas?",
    "answerOptions": [
      {
        "text": "A contract between a seller and a broker to market and sell property",
        "rationale": "A listing agreement is a contract between a seller and a broker to market and sell property.",
        "isCorrect": true
      },
      {
        "text": "A contract between a buyer and a broker to find property",
        "rationale": "This describes a buyer representation agreement, not a listing agreement.",
        "isCorrect": false
      },
      {
        "text": "A contract between two brokers",
        "rationale": "This describes a cooperation agreement, not a listing agreement.",
        "isCorrect": false
      },
      {
        "text": "A contract between a buyer and seller",
        "rationale": "This describes a purchase agreement, not a listing agreement.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the contract that authorizes a broker to sell property."
  },
  {
    "questionNumber": 68,
    "question": "What is an exclusive right to sell listing in Texas?",
    "answerOptions": [
      {
        "text": "A listing that guarantees the broker commission regardless of who finds the buyer",
        "rationale": "An exclusive right to sell listing guarantees the broker commission regardless of who finds the buyer.",
        "isCorrect": true
      },
      {
        "text": "A listing that only allows the broker to sell",
        "rationale": "This describes an exclusive agency listing, not an exclusive right to sell.",
        "isCorrect": false
      },
      {
        "text": "A listing that allows multiple brokers to sell",
        "rationale": "This describes an open listing, not an exclusive right to sell.",
        "isCorrect": false
      },
      {
        "text": "A listing that expires after 30 days",
        "rationale": "This describes a listing term, not the type of listing.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the listing that provides the most protection to the broker."
  },
  {
    "questionNumber": 69,
    "question": "What is an exclusive agency listing in Texas?",
    "answerOptions": [
      {
        "text": "A listing that gives the broker the right to sell but allows the seller to sell without commission",
        "rationale": "An exclusive agency listing gives the broker the right to sell but allows the seller to sell without commission.",
        "isCorrect": true
      },
      {
        "text": "A listing that guarantees the broker commission regardless of who finds the buyer",
        "rationale": "This describes an exclusive right to sell listing, not an exclusive agency listing.",
        "isCorrect": false
      },
      {
        "text": "A listing that allows multiple brokers to sell",
        "rationale": "This describes an open listing, not an exclusive agency listing.",
        "isCorrect": false
      },
      {
        "text": "A listing that expires after 30 days",
        "rationale": "This describes a listing term, not the type of listing.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a listing that gives the broker rights but allows seller exceptions."
  },
  {
    "questionNumber": 70,
    "question": "What is an open listing in Texas?",
    "answerOptions": [
      {
        "text": "A listing that allows multiple brokers to sell the property",
        "rationale": "An open listing allows multiple brokers to sell the property.",
        "isCorrect": true
      },
      {
        "text": "A listing that gives the broker the right to sell but allows the seller to sell without commission",
        "rationale": "This describes an exclusive agency listing, not an open listing.",
        "isCorrect": false
      },
      {
        "text": "A listing that guarantees the broker commission regardless of who finds the buyer",
        "rationale": "This describes an exclusive right to sell listing, not an open listing.",
        "isCorrect": false
      },
      {
        "text": "A listing that expires after 30 days",
        "rationale": "This describes a listing term, not the type of listing.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a listing that is available to multiple brokers."
  },
  {
    "questionNumber": 71,
    "question": "What is a net listing in Texas?",
    "answerOptions": [
      {
        "text": "A listing where the broker keeps any amount over the seller's net price",
        "rationale": "A net listing allows the broker to keep any amount over the seller's net price.",
        "isCorrect": true
      },
      {
        "text": "A listing with no commission",
        "rationale": "A net listing involves commission, not no commission.",
        "isCorrect": false
      },
      {
        "text": "A listing with a fixed commission",
        "rationale": "A net listing has variable commission, not fixed commission.",
        "isCorrect": false
      },
      {
        "text": "A listing with commission split between brokers",
        "rationale": "This describes commission splitting, not a net listing.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a listing where the broker's commission varies based on the sale price."
  },
  {
    "questionNumber": 72,
    "question": "What is a buyer representation agreement in Texas?",
    "answerOptions": [
      {
        "text": "A contract between a buyer and a broker to find and purchase property",
        "rationale": "A buyer representation agreement is a contract between a buyer and a broker to find and purchase property.",
        "isCorrect": true
      },
      {
        "text": "A contract between a seller and a broker to market and sell property",
        "rationale": "This describes a listing agreement, not a buyer representation agreement.",
        "isCorrect": false
      },
      {
        "text": "A contract between two brokers",
        "rationale": "This describes a cooperation agreement, not a buyer representation agreement.",
        "isCorrect": false
      },
      {
        "text": "A contract between a buyer and seller",
        "rationale": "This describes a purchase agreement, not a buyer representation agreement.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the contract that authorizes a broker to represent a buyer."
  },
  {
    "questionNumber": 73,
    "question": "What is a cooperation agreement in Texas?",
    "answerOptions": [
      {
        "text": "An agreement between brokers to cooperate in a transaction",
        "rationale": "A cooperation agreement is an agreement between brokers to cooperate in a transaction.",
        "isCorrect": true
      },
      {
        "text": "An agreement between a buyer and a broker",
        "rationale": "This describes a buyer representation agreement, not a cooperation agreement.",
        "isCorrect": false
      },
      {
        "text": "An agreement between a seller and a broker",
        "rationale": "This describes a listing agreement, not a cooperation agreement.",
        "isCorrect": false
      },
      {
        "text": "An agreement between a buyer and seller",
        "rationale": "This describes a purchase agreement, not a cooperation agreement.",
        "isCorrect": false
      }
    ],
    "hint": "Think about agreements between professionals to work together."
  },
  {
    "questionNumber": 74,
    "question": "What is procuring cause in Texas?",
    "answerOptions": [
      {
        "text": "The broker who brings together a willing buyer and seller",
        "rationale": "Procuring cause is the broker who brings together a willing buyer and seller.",
        "isCorrect": true
      },
      {
        "text": "The broker who lists the property",
        "rationale": "This describes the listing broker, not necessarily the procuring cause.",
        "isCorrect": false
      },
      {
        "text": "The broker who represents the buyer",
        "rationale": "This describes the buyer's broker, not necessarily the procuring cause.",
        "isCorrect": false
      },
      {
        "text": "The broker who closes the transaction",
        "rationale": "This describes the closing broker, not necessarily the procuring cause.",
        "isCorrect": false
      }
    ],
    "hint": "Think about who is responsible for bringing the parties together."
  },
  {
    "questionNumber": 75,
    "question": "What is commission in Texas real estate?",
    "answerOptions": [
      {
        "text": "The fee paid to a broker for services rendered",
        "rationale": "Commission is the fee paid to a broker for services rendered in a real estate transaction.",
        "isCorrect": true
      },
      {
        "text": "The fee paid to a lender for a loan",
        "rationale": "This describes loan fees, not commission.",
        "isCorrect": false
      },
      {
        "text": "The fee paid to an appraiser",
        "rationale": "This describes appraisal fees, not commission.",
        "isCorrect": false
      },
      {
        "text": "The fee paid to an inspector",
        "rationale": "This describes inspection fees, not commission.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the fee paid to real estate professionals for their services."
  },
  {
    "questionNumber": 76,
    "question": "What is commission splitting in Texas?",
    "answerOptions": [
      {
        "text": "Dividing commission between cooperating brokers",
        "rationale": "Commission splitting involves dividing commission between cooperating brokers.",
        "isCorrect": true
      },
      {
        "text": "Dividing commission between buyer and seller",
        "rationale": "Commission is not divided between buyer and seller; it's paid by the seller.",
        "isCorrect": false
      },
      {
        "text": "Dividing commission between broker and agent",
        "rationale": "This describes commission sharing, not commission splitting.",
        "isCorrect": false
      },
      {
        "text": "Dividing commission between listing and selling brokers",
        "rationale": "This describes commission splitting, but the answer is more general.",
        "isCorrect": false
      }
    ],
    "hint": "Think about how commission is divided between different brokers."
  },
  {
    "questionNumber": 77,
    "question": "What is a broker's responsibility to supervise licensees in Texas?",
    "answerOptions": [
      {
        "text": "To ensure licensees comply with laws and regulations",
        "rationale": "Brokers must supervise licensees to ensure compliance with laws and regulations.",
        "isCorrect": true
      },
      {
        "text": "To ensure licensees earn high commissions",
        "rationale": "Supervision is about compliance, not commission levels.",
        "isCorrect": false
      },
      {
        "text": "To ensure licensees work long hours",
        "rationale": "Supervision is about compliance, not work hours.",
        "isCorrect": false
      },
      {
        "text": "To ensure licensees only work with certain clients",
        "rationale": "Supervision is about compliance, not client selection.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what brokers must ensure regarding their licensees' conduct."
  },
  {
    "questionNumber": 78,
    "question": "What is a broker's responsibility to maintain records in Texas?",
    "answerOptions": [
      {
        "text": "To maintain records of all transactions and trust funds",
        "rationale": "Brokers must maintain records of all transactions and trust funds.",
        "isCorrect": true
      },
      {
        "text": "To maintain records of only successful transactions",
        "rationale": "Brokers must maintain records of all transactions, not just successful ones.",
        "isCorrect": false
      },
      {
        "text": "To maintain records of only commission payments",
        "rationale": "Brokers must maintain records of all transactions and trust funds, not just commissions.",
        "isCorrect": false
      },
      {
        "text": "To maintain records of only client information",
        "rationale": "Brokers must maintain records of all transactions and trust funds, not just client information.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what comprehensive records brokers must keep."
  },
  {
    "questionNumber": 79,
    "question": "What is a broker's responsibility to maintain trust funds in Texas?",
    "answerOptions": [
      {
        "text": "To maintain trust funds in separate accounts and not commingle with personal funds",
        "rationale": "Brokers must maintain trust funds in separate accounts and not commingle with personal funds.",
        "isCorrect": true
      },
      {
        "text": "To maintain trust funds in personal accounts",
        "rationale": "Trust funds must be kept in separate accounts, not personal accounts.",
        "isCorrect": false
      },
      {
        "text": "To maintain trust funds in business accounts",
        "rationale": "Trust funds must be kept in separate trust accounts, not general business accounts.",
        "isCorrect": false
      },
      {
        "text": "To maintain trust funds in investment accounts",
        "rationale": "Trust funds must be kept in separate trust accounts, not investment accounts.",
        "isCorrect": false
      }
    ],
    "hint": "Think about keeping client funds separate from personal funds."
  },
  {
    "questionNumber": 80,
    "question": "What is a broker's responsibility to maintain errors and omissions insurance in Texas?",
    "answerOptions": [
      {
        "text": "To maintain insurance to protect against professional liability claims",
        "rationale": "Brokers must maintain errors and omissions insurance to protect against professional liability claims.",
        "isCorrect": true
      },
      {
        "text": "To maintain insurance to protect against property damage",
        "rationale": "This describes property insurance, not errors and omissions insurance.",
        "isCorrect": false
      },
      {
        "text": "To maintain insurance to protect against theft",
        "rationale": "This describes theft insurance, not errors and omissions insurance.",
        "isCorrect": false
      },
      {
        "text": "To maintain insurance to protect against natural disasters",
        "rationale": "This describes disaster insurance, not errors and omissions insurance.",
        "isCorrect": false
      }
    ],
    "hint": "Think about insurance that protects against professional mistakes and negligence."
  },

  // BATCH 5: Texas Special Topics (Questions 81-100)
  {
    "questionNumber": 81,
    "question": "What is community property in Texas?",
    "answerOptions": [
      {
        "text": "Property acquired during marriage that is owned equally by both spouses",
        "rationale": "Community property is property acquired during marriage that is owned equally by both spouses.",
        "isCorrect": true
      },
      {
        "text": "Property owned by a homeowners association",
        "rationale": "This describes HOA property, not community property.",
        "isCorrect": false
      },
      {
        "text": "Property owned by the state",
        "rationale": "This describes public property, not community property.",
        "isCorrect": false
      },
      {
        "text": "Property owned by multiple people",
        "rationale": "This describes joint ownership, not community property.",
        "isCorrect": false
      }
    ],
    "hint": "Think about property ownership rules in marriage."
  },
  {
    "questionNumber": 82,
    "question": "What is separate property in Texas?",
    "answerOptions": [
      {
        "text": "Property owned by one spouse before marriage or acquired by gift or inheritance",
        "rationale": "Separate property is property owned by one spouse before marriage or acquired by gift or inheritance.",
        "isCorrect": true
      },
      {
        "text": "Property owned by both spouses",
        "rationale": "This describes community property, not separate property.",
        "isCorrect": false
      },
      {
        "text": "Property owned by a homeowners association",
        "rationale": "This describes HOA property, not separate property.",
        "isCorrect": false
      },
      {
        "text": "Property owned by the state",
        "rationale": "This describes public property, not separate property.",
        "isCorrect": false
      }
    ],
    "hint": "Think about property that belongs to only one spouse."
  },
  {
    "questionNumber": 83,
    "question": "What is a homeowners association (HOA) in Texas?",
    "answerOptions": [
      {
        "text": "An organization that manages common areas and enforces rules in a subdivision",
        "rationale": "An HOA manages common areas and enforces rules in a subdivision.",
        "isCorrect": true
      },
      {
        "text": "An organization that manages individual homes",
        "rationale": "An HOA manages common areas, not individual homes.",
        "isCorrect": false
      },
      {
        "text": "An organization that manages commercial properties",
        "rationale": "An HOA manages residential subdivisions, not commercial properties.",
        "isCorrect": false
      },
      {
        "text": "An organization that manages rental properties",
        "rationale": "An HOA manages subdivisions, not rental properties.",
        "isCorrect": false
      }
    ],
    "hint": "Think about organizations that manage shared areas in residential communities."
  },
  {
    "questionNumber": 84,
    "question": "What are HOA fees in Texas?",
    "answerOptions": [
      {
        "text": "Regular payments made by homeowners to maintain common areas and services",
        "rationale": "HOA fees are regular payments made by homeowners to maintain common areas and services.",
        "isCorrect": true
      },
      {
        "text": "One-time payments made when buying a home",
        "rationale": "HOA fees are ongoing payments, not one-time payments.",
        "isCorrect": false
      },
      {
        "text": "Payments made only when violations occur",
        "rationale": "HOA fees are regular payments, not violation-based payments.",
        "isCorrect": false
      },
      {
        "text": "Payments made only by renters",
        "rationale": "HOA fees are paid by homeowners, not renters.",
        "isCorrect": false
      }
    ],
    "hint": "Think about ongoing payments for community maintenance."
  },
  {
    "questionNumber": 85,
    "question": "What are HOA restrictions in Texas?",
    "answerOptions": [
      {
        "text": "Rules and regulations that govern property use and appearance",
        "rationale": "HOA restrictions are rules and regulations that govern property use and appearance.",
        "isCorrect": true
      },
      {
        "text": "Rules that govern only commercial properties",
        "rationale": "HOA restrictions apply to residential properties, not commercial properties.",
        "isCorrect": false
      },
      {
        "text": "Rules that govern only rental properties",
        "rationale": "HOA restrictions apply to all properties in the subdivision, not just rentals.",
        "isCorrect": false
      },
      {
        "text": "Rules that govern only common areas",
        "rationale": "HOA restrictions apply to individual properties, not just common areas.",
        "isCorrect": false
      }
    ],
    "hint": "Think about rules that control how properties can be used and maintained."
  },
  {
    "questionNumber": 86,
    "question": "What is landlord-tenant law in Texas?",
    "answerOptions": [
      {
        "text": "Laws that govern the relationship between property owners and renters",
        "rationale": "Landlord-tenant law governs the relationship between property owners and renters.",
        "isCorrect": true
      },
      {
        "text": "Laws that govern only commercial leases",
        "rationale": "Landlord-tenant law applies to both residential and commercial leases.",
        "isCorrect": false
      },
      {
        "text": "Laws that govern only residential leases",
        "rationale": "Landlord-tenant law applies to both residential and commercial leases.",
        "isCorrect": false
      },
      {
        "text": "Laws that govern only short-term rentals",
        "rationale": "Landlord-tenant law applies to all types of rental agreements.",
        "isCorrect": false
      }
    ],
    "hint": "Think about laws that regulate rental relationships."
  },
  {
    "questionNumber": 87,
    "question": "What is a lease agreement in Texas?",
    "answerOptions": [
      {
        "text": "A contract between a landlord and tenant for the use of property",
        "rationale": "A lease agreement is a contract between a landlord and tenant for the use of property.",
        "isCorrect": true
      },
      {
        "text": "A contract between a buyer and seller",
        "rationale": "This describes a purchase agreement, not a lease agreement.",
        "isCorrect": false
      },
      {
        "text": "A contract between two landlords",
        "rationale": "This describes a management agreement, not a lease agreement.",
        "isCorrect": false
      },
      {
        "text": "A contract between two tenants",
        "rationale": "This describes a sublease agreement, not a lease agreement.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the contract that allows someone to use property in exchange for payment."
  },
  {
    "questionNumber": 88,
    "question": "What is a security deposit in Texas?",
    "answerOptions": [
      {
        "text": "Money held by the landlord to cover damages or unpaid rent",
        "rationale": "A security deposit is money held by the landlord to cover damages or unpaid rent.",
        "isCorrect": true
      },
      {
        "text": "Money paid to the real estate agent",
        "rationale": "This describes commission, not a security deposit.",
        "isCorrect": false
      },
      {
        "text": "Money paid to the lender",
        "rationale": "This describes loan fees, not a security deposit.",
        "isCorrect": false
      },
      {
        "text": "Money paid to the appraiser",
        "rationale": "This describes appraisal fees, not a security deposit.",
        "isCorrect": false
      }
    ],
    "hint": "Think about money held as protection against potential problems."
  },
  {
    "questionNumber": 89,
    "question": "What is eviction in Texas?",
    "answerOptions": [
      {
        "text": "The legal process to remove a tenant from rental property",
        "rationale": "Eviction is the legal process to remove a tenant from rental property.",
        "isCorrect": true
      },
      {
        "text": "The process of selling rental property",
        "rationale": "This describes selling property, not eviction.",
        "isCorrect": false
      },
      {
        "text": "The process of renovating rental property",
        "rationale": "This describes renovation, not eviction.",
        "isCorrect": false
      },
      {
        "text": "The process of renting property",
        "rationale": "This describes renting property, not eviction.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the legal process to force someone to leave rental property."
  },
  {
    "questionNumber": 90,
    "question": "What is a lien in Texas?",
    "answerOptions": [
      {
        "text": "A legal claim against property for payment of a debt",
        "rationale": "A lien is a legal claim against property for payment of a debt.",
        "isCorrect": true
      },
      {
        "text": "A legal claim against a person",
        "rationale": "A lien is against property, not a person.",
        "isCorrect": false
      },
      {
        "text": "A legal claim against a business",
        "rationale": "A lien is against property, not a business.",
        "isCorrect": false
      },
      {
        "text": "A legal claim against income",
        "rationale": "A lien is against property, not income.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a legal claim that attaches to property."
  },
  {
    "questionNumber": 91,
    "question": "What is a mechanic's lien in Texas?",
    "answerOptions": [
      {
        "text": "A lien filed by contractors or suppliers for unpaid work or materials",
        "rationale": "A mechanic's lien is filed by contractors or suppliers for unpaid work or materials.",
        "isCorrect": true
      },
      {
        "text": "A lien filed by the government for unpaid taxes",
        "rationale": "This describes a tax lien, not a mechanic's lien.",
        "isCorrect": false
      },
      {
        "text": "A lien filed by lenders for unpaid loans",
        "rationale": "This describes a mortgage lien, not a mechanic's lien.",
        "isCorrect": false
      },
      {
        "text": "A lien filed by homeowners associations for unpaid fees",
        "rationale": "This describes an HOA lien, not a mechanic's lien.",
        "isCorrect": false
      }
    ],
    "hint": "Think about liens filed by people who provide work or materials."
  },
  {
    "questionNumber": 92,
    "question": "What is a tax lien in Texas?",
    "answerOptions": [
      {
        "text": "A lien filed by the government for unpaid taxes",
        "rationale": "A tax lien is filed by the government for unpaid taxes.",
        "isCorrect": true
      },
      {
        "text": "A lien filed by contractors for unpaid work",
        "rationale": "This describes a mechanic's lien, not a tax lien.",
        "isCorrect": false
      },
      {
        "text": "A lien filed by lenders for unpaid loans",
        "rationale": "This describes a mortgage lien, not a tax lien.",
        "isCorrect": false
      },
      {
        "text": "A lien filed by homeowners associations for unpaid fees",
        "rationale": "This describes an HOA lien, not a tax lien.",
        "isCorrect": false
      }
    ],
    "hint": "Think about liens filed by government entities for unpaid taxes."
  },
  {
    "questionNumber": 93,
    "question": "What is a mortgage lien in Texas?",
    "answerOptions": [
      {
        "text": "A lien created by a mortgage loan secured by real property",
        "rationale": "A mortgage lien is created by a mortgage loan secured by real property.",
        "isCorrect": true
      },
      {
        "text": "A lien filed by the government for unpaid taxes",
        "rationale": "This describes a tax lien, not a mortgage lien.",
        "isCorrect": false
      },
      {
        "text": "A lien filed by contractors for unpaid work",
        "rationale": "This describes a mechanic's lien, not a mortgage lien.",
        "isCorrect": false
      },
      {
        "text": "A lien filed by homeowners associations for unpaid fees",
        "rationale": "This describes an HOA lien, not a mortgage lien.",
        "isCorrect": false
      }
    ],
    "hint": "Think about liens created by loans secured by real estate."
  },
  {
    "questionNumber": 94,
    "question": "What is lien priority in Texas?",
    "answerOptions": [
      {
        "text": "The order in which liens are paid when property is sold",
        "rationale": "Lien priority determines the order in which liens are paid when property is sold.",
        "isCorrect": true
      },
      {
        "text": "The amount of money owed on a lien",
        "rationale": "This describes lien amount, not lien priority.",
        "isCorrect": false
      },
      {
        "text": "The date a lien was filed",
        "rationale": "This describes lien date, not lien priority.",
        "isCorrect": false
      },
      {
        "text": "The type of lien filed",
        "rationale": "This describes lien type, not lien priority.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the order in which different liens get paid."
  },
  {
    "questionNumber": 95,
    "question": "What is foreclosure in Texas?",
    "answerOptions": [
      {
        "text": "The legal process to sell property to satisfy a lien",
        "rationale": "Foreclosure is the legal process to sell property to satisfy a lien.",
        "isCorrect": true
      },
      {
        "text": "The process of buying property",
        "rationale": "This describes purchasing property, not foreclosure.",
        "isCorrect": false
      },
      {
        "text": "The process of selling property",
        "rationale": "This describes selling property, not foreclosure.",
        "isCorrect": false
      },
      {
        "text": "The process of renting property",
        "rationale": "This describes renting property, not foreclosure.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the legal process to force a sale to pay debts."
  },
  {
    "questionNumber": 96,
    "question": "What is a deed of trust in Texas?",
    "answerOptions": [
      {
        "text": "A document that creates a lien on property to secure a loan",
        "rationale": "A deed of trust creates a lien on property to secure a loan.",
        "isCorrect": true
      },
      {
        "text": "A document that transfers ownership of property",
        "rationale": "This describes a deed, not a deed of trust.",
        "isCorrect": false
      },
      {
        "text": "A document that creates a lease on property",
        "rationale": "This describes a lease agreement, not a deed of trust.",
        "isCorrect": false
      },
      {
        "text": "A document that creates an easement on property",
        "rationale": "This describes an easement agreement, not a deed of trust.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a document that secures a loan with property."
  },
  {
    "questionNumber": 97,
    "question": "What is a promissory note in Texas?",
    "answerOptions": [
      {
        "text": "A document that promises to repay a loan",
        "rationale": "A promissory note is a document that promises to repay a loan.",
        "isCorrect": true
      },
      {
        "text": "A document that creates a lien on property",
        "rationale": "This describes a deed of trust, not a promissory note.",
        "isCorrect": false
      },
      {
        "text": "A document that transfers ownership of property",
        "rationale": "This describes a deed, not a promissory note.",
        "isCorrect": false
      },
      {
        "text": "A document that creates a lease on property",
        "rationale": "This describes a lease agreement, not a promissory note.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a document that contains a promise to pay."
  },
  {
    "questionNumber": 98,
    "question": "What is a deed of trust sale in Texas?",
    "answerOptions": [
      {
        "text": "A sale of property to satisfy a deed of trust lien",
        "rationale": "A deed of trust sale is a sale of property to satisfy a deed of trust lien.",
        "isCorrect": true
      },
      {
        "text": "A sale of property by the owner",
        "rationale": "This describes a regular sale, not a deed of trust sale.",
        "isCorrect": false
      },
      {
        "text": "A sale of property by a real estate agent",
        "rationale": "This describes an agent-assisted sale, not a deed of trust sale.",
        "isCorrect": false
      },
      {
        "text": "A sale of property by a court",
        "rationale": "This describes a court-ordered sale, not a deed of trust sale.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a sale that occurs to satisfy a loan secured by property."
  },
  {
    "questionNumber": 99,
    "question": "What is a deficiency judgment in Texas?",
    "answerOptions": [
      {
        "text": "A judgment for the amount owed after foreclosure sale proceeds",
        "rationale": "A deficiency judgment is for the amount owed after foreclosure sale proceeds.",
        "isCorrect": true
      },
      {
        "text": "A judgment for the full amount of the loan",
        "rationale": "A deficiency judgment is only for the amount not covered by sale proceeds.",
        "isCorrect": false
      },
      {
        "text": "A judgment for the amount of the down payment",
        "rationale": "A deficiency judgment is not related to the down payment.",
        "isCorrect": false
      },
      {
        "text": "A judgment for the amount of the monthly payment",
        "rationale": "A deficiency judgment is not related to monthly payments.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what happens when a foreclosure sale doesn't cover the full debt."
  },
  {
    "questionNumber": 100,
    "question": "What is a redemption period in Texas?",
    "answerOptions": [
      {
        "text": "The time period during which a borrower can reclaim property after foreclosure",
        "rationale": "A redemption period is the time during which a borrower can reclaim property after foreclosure.",
        "isCorrect": true
      },
      {
        "text": "The time period during which a buyer can cancel a contract",
        "rationale": "This describes a rescission period, not a redemption period.",
        "isCorrect": false
      },
      {
        "text": "The time period during which a seller can cancel a contract",
        "rationale": "This describes a cancellation period, not a redemption period.",
        "isCorrect": false
      },
      {
        "text": "The time period during which a tenant can renew a lease",
        "rationale": "This describes a renewal period, not a redemption period.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the time period to reclaim property after losing it."
  },

  // BATCH 6: National Real Estate - Property Characteristics (Questions 101-120)
  {
    "questionNumber": 101,
    "question": "What is real property in real estate?",
    "answerOptions": [
      {
        "text": "Land and everything permanently attached to it",
        "rationale": "Real property includes land and everything permanently attached to it.",
        "isCorrect": true
      },
      {
        "text": "Only the land itself",
        "rationale": "Real property includes more than just land; it includes permanent attachments.",
        "isCorrect": false
      },
      {
        "text": "Only buildings on the land",
        "rationale": "Real property includes land and buildings, not just buildings.",
        "isCorrect": false
      },
      {
        "text": "Only personal property",
        "rationale": "Real property is different from personal property.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what constitutes real estate ownership."
  },
  {
    "questionNumber": 102,
    "question": "What is personal property in real estate?",
    "answerOptions": [
      {
        "text": "Movable items that are not permanently attached to land",
        "rationale": "Personal property consists of movable items that are not permanently attached to land.",
        "isCorrect": true
      },
      {
        "text": "Land and buildings",
        "rationale": "This describes real property, not personal property.",
        "isCorrect": false
      },
      {
        "text": "Items permanently attached to land",
        "rationale": "This describes fixtures, which are part of real property.",
        "isCorrect": false
      },
      {
        "text": "Only vehicles",
        "rationale": "Personal property includes more than just vehicles.",
        "isCorrect": false
      }
    ],
    "hint": "Think about items that can be moved and are not part of the real estate."
  },
  {
    "questionNumber": 103,
    "question": "What is a fixture in real estate?",
    "answerOptions": [
      {
        "text": "Personal property that has become permanently attached to real property",
        "rationale": "A fixture is personal property that has become permanently attached to real property.",
        "isCorrect": true
      },
      {
        "text": "Personal property that can be easily moved",
        "rationale": "This describes personal property, not fixtures.",
        "isCorrect": false
      },
      {
        "text": "Real property that can be easily moved",
        "rationale": "Real property cannot be easily moved.",
        "isCorrect": false
      },
      {
        "text": "Only appliances",
        "rationale": "Fixtures include more than just appliances.",
        "isCorrect": false
      }
    ],
    "hint": "Think about items that start as personal property but become part of real estate."
  },
  {
    "questionNumber": 104,
    "question": "What is the test for determining if something is a fixture?",
    "answerOptions": [
      {
        "text": "Method of attachment, adaptation to the property, and intent of the parties",
        "rationale": "The test for fixtures considers method of attachment, adaptation to the property, and intent of the parties.",
        "isCorrect": true
      },
      {
        "text": "Only the method of attachment",
        "rationale": "The test considers multiple factors, not just attachment method.",
        "isCorrect": false
      },
      {
        "text": "Only the cost of the item",
        "rationale": "Cost is not part of the fixture test.",
        "isCorrect": false
      },
      {
        "text": "Only the age of the item",
        "rationale": "Age is not part of the fixture test.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the multiple factors that determine if something becomes part of real estate."
  },
  {
    "questionNumber": 105,
    "question": "What is a trade fixture in real estate?",
    "answerOptions": [
      {
        "text": "Equipment used in a business that can be removed by the tenant",
        "rationale": "A trade fixture is equipment used in a business that can be removed by the tenant.",
        "isCorrect": true
      },
      {
        "text": "Equipment that becomes part of the real property",
        "rationale": "This describes a regular fixture, not a trade fixture.",
        "isCorrect": false
      },
      {
        "text": "Equipment owned by the landlord",
        "rationale": "Trade fixtures are owned by the tenant, not the landlord.",
        "isCorrect": false
      },
      {
        "text": "Equipment that cannot be moved",
        "rationale": "Trade fixtures can be moved by the tenant.",
        "isCorrect": false
      }
    ],
    "hint": "Think about business equipment that tenants can take with them."
  },
  {
    "questionNumber": 106,
    "question": "What is an easement in real estate?",
    "answerOptions": [
      {
        "text": "The right to use another person's land for a specific purpose",
        "rationale": "An easement is the right to use another person's land for a specific purpose.",
        "isCorrect": true
      },
      {
        "text": "The right to own another person's land",
        "rationale": "This describes ownership, not an easement.",
        "isCorrect": false
      },
      {
        "text": "The right to sell another person's land",
        "rationale": "This describes selling rights, not an easement.",
        "isCorrect": false
      },
      {
        "text": "The right to rent another person's land",
        "rationale": "This describes rental rights, not an easement.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the right to use someone else's property for a specific purpose."
  },
  {
    "questionNumber": 107,
    "question": "What is an appurtenant easement in real estate?",
    "answerOptions": [
      {
        "text": "An easement that benefits a specific piece of land",
        "rationale": "An appurtenant easement benefits a specific piece of land and runs with the land.",
        "isCorrect": true
      },
      {
        "text": "An easement that benefits a specific person",
        "rationale": "This describes a personal easement, not an appurtenant easement.",
        "isCorrect": false
      },
      {
        "text": "An easement that can be revoked at any time",
        "rationale": "Appurtenant easements run with the land and cannot be easily revoked.",
        "isCorrect": false
      },
      {
        "text": "An easement that expires after a certain time",
        "rationale": "Appurtenant easements typically do not expire.",
        "isCorrect": false
      }
    ],
    "hint": "Think about easements that are attached to and benefit specific land."
  },
  {
    "questionNumber": 108,
    "question": "What is a personal easement in real estate?",
    "answerOptions": [
      {
        "text": "An easement that benefits a specific person, not the land",
        "rationale": "A personal easement benefits a specific person and does not run with the land.",
        "isCorrect": true
      },
      {
        "text": "An easement that benefits a specific piece of land",
        "rationale": "This describes an appurtenant easement, not a personal easement.",
        "isCorrect": false
      },
      {
        "text": "An easement that can be transferred to anyone",
        "rationale": "Personal easements are typically not transferable.",
        "isCorrect": false
      },
      {
        "text": "An easement that lasts forever",
        "rationale": "Personal easements typically have limited duration.",
        "isCorrect": false
      }
    ],
    "hint": "Think about easements that benefit a person rather than land."
  },
  {
    "questionNumber": 109,
    "question": "What is a license in real estate?",
    "answerOptions": [
      {
        "text": "Permission to use another person's land that can be revoked",
        "rationale": "A license is permission to use another person's land that can be revoked at any time.",
        "isCorrect": true
      },
      {
        "text": "The right to use another person's land permanently",
        "rationale": "This describes an easement, not a license.",
        "isCorrect": false
      },
      {
        "text": "The right to own another person's land",
        "rationale": "This describes ownership, not a license.",
        "isCorrect": false
      },
      {
        "text": "The right to sell another person's land",
        "rationale": "This describes selling rights, not a license.",
        "isCorrect": false
      }
    ],
    "hint": "Think about temporary permission that can be taken away."
  },
  {
    "questionNumber": 110,
    "question": "What is a lien in real estate?",
    "answerOptions": [
      {
        "text": "A legal claim against property for payment of a debt",
        "rationale": "A lien is a legal claim against property for payment of a debt.",
        "isCorrect": true
      },
      {
        "text": "A legal claim against a person",
        "rationale": "A lien is against property, not a person.",
        "isCorrect": false
      },
      {
        "text": "A legal claim against a business",
        "rationale": "A lien is against property, not a business.",
        "isCorrect": false
      },
      {
        "text": "A legal claim against income",
        "rationale": "A lien is against property, not income.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a legal claim that attaches to property."
  },
  {
    "questionNumber": 111,
    "question": "What is a voluntary lien in real estate?",
    "answerOptions": [
      {
        "text": "A lien created by agreement, such as a mortgage",
        "rationale": "A voluntary lien is created by agreement, such as a mortgage.",
        "isCorrect": true
      },
      {
        "text": "A lien created by law, such as a tax lien",
        "rationale": "This describes an involuntary lien, not a voluntary lien.",
        "isCorrect": false
      },
      {
        "text": "A lien created by a court order",
        "rationale": "This describes an involuntary lien, not a voluntary lien.",
        "isCorrect": false
      },
      {
        "text": "A lien created by a government agency",
        "rationale": "This describes an involuntary lien, not a voluntary lien.",
        "isCorrect": false
      }
    ],
    "hint": "Think about liens that are created by agreement between parties."
  },
  {
    "questionNumber": 112,
    "question": "What is an involuntary lien in real estate?",
    "answerOptions": [
      {
        "text": "A lien created by law or court order without the property owner's consent",
        "rationale": "An involuntary lien is created by law or court order without the property owner's consent.",
        "isCorrect": true
      },
      {
        "text": "A lien created by agreement, such as a mortgage",
        "rationale": "This describes a voluntary lien, not an involuntary lien.",
        "isCorrect": false
      },
      {
        "text": "A lien created by the property owner",
        "rationale": "This describes a voluntary lien, not an involuntary lien.",
        "isCorrect": false
      },
      {
        "text": "A lien created by a real estate agent",
        "rationale": "Real estate agents do not create liens.",
        "isCorrect": false
      }
    ],
    "hint": "Think about liens that are created without the property owner's agreement."
  },
  {
    "questionNumber": 113,
    "question": "What is a specific lien in real estate?",
    "answerOptions": [
      {
        "text": "A lien against a specific piece of property",
        "rationale": "A specific lien is against a specific piece of property.",
        "isCorrect": true
      },
      {
        "text": "A lien against all of a person's property",
        "rationale": "This describes a general lien, not a specific lien.",
        "isCorrect": false
      },
      {
        "text": "A lien against a person's income",
        "rationale": "This describes a lien against income, not a specific lien.",
        "isCorrect": false
      },
      {
        "text": "A lien against a person's business",
        "rationale": "This describes a lien against a business, not a specific lien.",
        "isCorrect": false
      }
    ],
    "hint": "Think about liens that attach to specific property."
  },
  {
    "questionNumber": 114,
    "question": "What is a general lien in real estate?",
    "answerOptions": [
      {
        "text": "A lien against all of a person's property",
        "rationale": "A general lien is against all of a person's property.",
        "isCorrect": true
      },
      {
        "text": "A lien against a specific piece of property",
        "rationale": "This describes a specific lien, not a general lien.",
        "isCorrect": false
      },
      {
        "text": "A lien against a person's income",
        "rationale": "This describes a lien against income, not a general lien.",
        "isCorrect": false
      },
      {
        "text": "A lien against a person's business",
        "rationale": "This describes a lien against a business, not a general lien.",
        "isCorrect": false
      }
    ],
    "hint": "Think about liens that attach to all of someone's property."
  },
  {
    "questionNumber": 115,
    "question": "What is a lien priority in real estate?",
    "answerOptions": [
      {
        "text": "The order in which liens are paid when property is sold",
        "rationale": "Lien priority determines the order in which liens are paid when property is sold.",
        "isCorrect": true
      },
      {
        "text": "The amount of money owed on a lien",
        "rationale": "This describes lien amount, not lien priority.",
        "isCorrect": false
      },
      {
        "text": "The date a lien was filed",
        "rationale": "This describes lien date, not lien priority.",
        "isCorrect": false
      },
      {
        "text": "The type of lien filed",
        "rationale": "This describes lien type, not lien priority.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the order in which different liens get paid."
  },
  {
    "questionNumber": 116,
    "question": "What is a lien waiver in real estate?",
    "answerOptions": [
      {
        "text": "A document that releases a lien against property",
        "rationale": "A lien waiver is a document that releases a lien against property.",
        "isCorrect": true
      },
      {
        "text": "A document that creates a lien against property",
        "rationale": "This describes a lien creation document, not a lien waiver.",
        "isCorrect": false
      },
      {
        "text": "A document that transfers a lien to another party",
        "rationale": "This describes a lien transfer document, not a lien waiver.",
        "isCorrect": false
      },
      {
        "text": "A document that increases a lien amount",
        "rationale": "This describes a lien modification document, not a lien waiver.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a document that removes a lien from property."
  },
  {
    "questionNumber": 117,
    "question": "What is a lien release in real estate?",
    "answerOptions": [
      {
        "text": "A document that removes a lien from property",
        "rationale": "A lien release is a document that removes a lien from property.",
        "isCorrect": true
      },
      {
        "text": "A document that creates a lien against property",
        "rationale": "This describes a lien creation document, not a lien release.",
        "isCorrect": false
      },
      {
        "text": "A document that transfers a lien to another party",
        "rationale": "This describes a lien transfer document, not a lien release.",
        "isCorrect": false
      },
      {
        "text": "A document that increases a lien amount",
        "rationale": "This describes a lien modification document, not a lien release.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a document that removes a lien from property."
  },
  {
    "questionNumber": 118,
    "question": "What is a lien satisfaction in real estate?",
    "answerOptions": [
      {
        "text": "A document that confirms a lien has been paid and released",
        "rationale": "A lien satisfaction confirms that a lien has been paid and released.",
        "isCorrect": true
      },
      {
        "text": "A document that creates a lien against property",
        "rationale": "This describes a lien creation document, not a lien satisfaction.",
        "isCorrect": false
      },
      {
        "text": "A document that transfers a lien to another party",
        "rationale": "This describes a lien transfer document, not a lien satisfaction.",
        "isCorrect": false
      },
      {
        "text": "A document that increases a lien amount",
        "rationale": "This describes a lien modification document, not a lien satisfaction.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a document that confirms a lien has been satisfied."
  },
  {
    "questionNumber": 119,
    "question": "What is a lien search in real estate?",
    "answerOptions": [
      {
        "text": "A search to identify all liens against a property",
        "rationale": "A lien search identifies all liens against a property.",
        "isCorrect": true
      },
      {
        "text": "A search to identify all owners of a property",
        "rationale": "This describes a title search, not a lien search.",
        "isCorrect": false
      },
      {
        "text": "A search to identify all easements on a property",
        "rationale": "This describes an easement search, not a lien search.",
        "isCorrect": false
      },
      {
        "text": "A search to identify all restrictions on a property",
        "rationale": "This describes a restriction search, not a lien search.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a search that identifies all claims against property."
  },
  {
    "questionNumber": 120,
    "question": "What is a lien title insurance in real estate?",
    "answerOptions": [
      {
        "text": "Insurance that protects against undisclosed liens",
        "rationale": "Lien title insurance protects against undisclosed liens.",
        "isCorrect": true
      },
      {
        "text": "Insurance that protects against property damage",
        "rationale": "This describes property insurance, not lien title insurance.",
        "isCorrect": false
      },
      {
        "text": "Insurance that protects against theft",
        "rationale": "This describes theft insurance, not lien title insurance.",
        "isCorrect": false
      },
      {
        "text": "Insurance that protects against natural disasters",
        "rationale": "This describes disaster insurance, not lien title insurance.",
        "isCorrect": false
      }
    ],
    "hint": "Think about insurance that protects against hidden liens."
  },

  // BATCH 7: National Real Estate - Forms of Ownership (Questions 121-140)
  {
    "questionNumber": 121,
    "question": "What is fee simple absolute in real estate?",
    "answerOptions": [
      {
        "text": "The highest form of ownership with no limitations",
        "rationale": "Fee simple absolute is the highest form of ownership with no limitations or conditions.",
        "isCorrect": true
      },
      {
        "text": "Ownership with limitations and conditions",
        "rationale": "This describes defeasible estates, not fee simple absolute.",
        "isCorrect": false
      },
      {
        "text": "Ownership for a limited time period",
        "rationale": "This describes leasehold estates, not fee simple absolute.",
        "isCorrect": false
      },
      {
        "text": "Ownership shared with others",
        "rationale": "This describes co-ownership, not fee simple absolute.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the most complete form of property ownership."
  },
  {
    "questionNumber": 122,
    "question": "What is a defeasible estate in real estate?",
    "answerOptions": [
      {
        "text": "An estate that can be terminated upon the occurrence of a specified event",
        "rationale": "A defeasible estate can be terminated upon the occurrence of a specified event.",
        "isCorrect": true
      },
      {
        "text": "An estate that cannot be terminated",
        "rationale": "This describes fee simple absolute, not a defeasible estate.",
        "isCorrect": false
      },
      {
        "text": "An estate that lasts for a specific time period",
        "rationale": "This describes leasehold estates, not defeasible estates.",
        "isCorrect": false
      },
      {
        "text": "An estate that is shared with others",
        "rationale": "This describes co-ownership, not defeasible estates.",
        "isCorrect": false
      }
    ],
    "hint": "Think about ownership that can be lost under certain conditions."
  },
  {
    "questionNumber": 123,
    "question": "What is a life estate in real estate?",
    "answerOptions": [
      {
        "text": "An estate that lasts for the lifetime of a specified person",
        "rationale": "A life estate lasts for the lifetime of a specified person.",
        "isCorrect": true
      },
      {
        "text": "An estate that lasts forever",
        "rationale": "This describes fee simple absolute, not a life estate.",
        "isCorrect": false
      },
      {
        "text": "An estate that lasts for a specific number of years",
        "rationale": "This describes leasehold estates, not life estates.",
        "isCorrect": false
      },
      {
        "text": "An estate that is shared with others",
        "rationale": "This describes co-ownership, not life estates.",
        "isCorrect": false
      }
    ],
    "hint": "Think about ownership that lasts for someone's lifetime."
  },
  {
    "questionNumber": 124,
    "question": "What is a remainder interest in real estate?",
    "answerOptions": [
      {
        "text": "The right to own property after a life estate ends",
        "rationale": "A remainder interest is the right to own property after a life estate ends.",
        "isCorrect": true
      },
      {
        "text": "The right to use property during a life estate",
        "rationale": "This describes the life estate itself, not a remainder interest.",
        "isCorrect": false
      },
      {
        "text": "The right to sell property during a life estate",
        "rationale": "This describes the life tenant's rights, not a remainder interest.",
        "isCorrect": false
      },
      {
        "text": "The right to rent property during a life estate",
        "rationale": "This describes rental rights, not a remainder interest.",
        "isCorrect": false
      }
    ],
    "hint": "Think about who gets the property after a life estate ends."
  },
  {
    "questionNumber": 125,
    "question": "What is a reversion interest in real estate?",
    "answerOptions": [
      {
        "text": "The right of the original owner to regain property after a life estate ends",
        "rationale": "A reversion interest is the right of the original owner to regain property after a life estate ends.",
        "isCorrect": true
      },
      {
        "text": "The right to use property during a life estate",
        "rationale": "This describes the life estate itself, not a reversion interest.",
        "isCorrect": false
      },
      {
        "text": "The right to sell property during a life estate",
        "rationale": "This describes the life tenant's rights, not a reversion interest.",
        "isCorrect": false
      },
      {
        "text": "The right to rent property during a life estate",
        "rationale": "This describes rental rights, not a reversion interest.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the original owner's right to get the property back."
  },
  {
    "questionNumber": 126,
    "question": "What is joint tenancy in real estate?",
    "answerOptions": [
      {
        "text": "Co-ownership with right of survivorship",
        "rationale": "Joint tenancy is co-ownership with right of survivorship.",
        "isCorrect": true
      },
      {
        "text": "Co-ownership without right of survivorship",
        "rationale": "This describes tenancy in common, not joint tenancy.",
        "isCorrect": false
      },
      {
        "text": "Co-ownership for a specific time period",
        "rationale": "This describes leasehold estates, not joint tenancy.",
        "isCorrect": false
      },
      {
        "text": "Co-ownership with unequal shares",
        "rationale": "Joint tenancy requires equal shares.",
        "isCorrect": false
      }
    ],
    "hint": "Think about co-ownership where surviving owners inherit the deceased owner's share."
  },
  {
    "questionNumber": 127,
    "question": "What is tenancy in common in real estate?",
    "answerOptions": [
      {
        "text": "Co-ownership without right of survivorship",
        "rationale": "Tenancy in common is co-ownership without right of survivorship.",
        "isCorrect": true
      },
      {
        "text": "Co-ownership with right of survivorship",
        "rationale": "This describes joint tenancy, not tenancy in common.",
        "isCorrect": false
      },
      {
        "text": "Co-ownership for a specific time period",
        "rationale": "This describes leasehold estates, not tenancy in common.",
        "isCorrect": false
      },
      {
        "text": "Co-ownership with equal shares only",
        "rationale": "Tenancy in common allows unequal shares.",
        "isCorrect": false
      }
    ],
    "hint": "Think about co-ownership where each owner's share passes to their heirs."
  },
  {
    "questionNumber": 128,
    "question": "What is tenancy by the entirety in real estate?",
    "answerOptions": [
      {
        "text": "Co-ownership between spouses with right of survivorship",
        "rationale": "Tenancy by the entirety is co-ownership between spouses with right of survivorship.",
        "isCorrect": true
      },
      {
        "text": "Co-ownership between any two people",
        "rationale": "Tenancy by the entirety is only between spouses.",
        "isCorrect": false
      },
      {
        "text": "Co-ownership without right of survivorship",
        "rationale": "Tenancy by the entirety includes right of survivorship.",
        "isCorrect": false
      },
      {
        "text": "Co-ownership for a specific time period",
        "rationale": "This describes leasehold estates, not tenancy by the entirety.",
        "isCorrect": false
      }
    ],
    "hint": "Think about co-ownership that's only available to married couples."
  },
  {
    "questionNumber": 129,
    "question": "What is a leasehold estate in real estate?",
    "answerOptions": [
      {
        "text": "An estate that lasts for a specific time period",
        "rationale": "A leasehold estate lasts for a specific time period.",
        "isCorrect": true
      },
      {
        "text": "An estate that lasts forever",
        "rationale": "This describes fee simple absolute, not a leasehold estate.",
        "isCorrect": false
      },
      {
        "text": "An estate that lasts for someone's lifetime",
        "rationale": "This describes life estates, not leasehold estates.",
        "isCorrect": false
      },
      {
        "text": "An estate that is shared with others",
        "rationale": "This describes co-ownership, not leasehold estates.",
        "isCorrect": false
      }
    ],
    "hint": "Think about temporary ownership rights through a lease."
  },
  {
    "questionNumber": 130,
    "question": "What is an estate for years in real estate?",
    "answerOptions": [
      {
        "text": "A leasehold estate with a specific beginning and ending date",
        "rationale": "An estate for years is a leasehold estate with a specific beginning and ending date.",
        "isCorrect": true
      },
      {
        "text": "A leasehold estate that can be terminated at any time",
        "rationale": "This describes an estate at will, not an estate for years.",
        "isCorrect": false
      },
      {
        "text": "A leasehold estate that renews automatically",
        "rationale": "This describes an estate from period to period, not an estate for years.",
        "isCorrect": false
      },
      {
        "text": "A leasehold estate that lasts forever",
        "rationale": "This describes fee simple absolute, not an estate for years.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a lease with fixed start and end dates."
  },
  {
    "questionNumber": 131,
    "question": "What is an estate at will in real estate?",
    "answerOptions": [
      {
        "text": "A leasehold estate that can be terminated at any time",
        "rationale": "An estate at will is a leasehold estate that can be terminated at any time.",
        "isCorrect": true
      },
      {
        "text": "A leasehold estate with a specific beginning and ending date",
        "rationale": "This describes an estate for years, not an estate at will.",
        "isCorrect": false
      },
      {
        "text": "A leasehold estate that renews automatically",
        "rationale": "This describes an estate from period to period, not an estate at will.",
        "isCorrect": false
      },
      {
        "text": "A leasehold estate that lasts forever",
        "rationale": "This describes fee simple absolute, not an estate at will.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a lease that can be ended by either party at any time."
  },
  {
    "questionNumber": 132,
    "question": "What is an estate from period to period in real estate?",
    "answerOptions": [
      {
        "text": "A leasehold estate that renews automatically",
        "rationale": "An estate from period to period is a leasehold estate that renews automatically.",
        "isCorrect": true
      },
      {
        "text": "A leasehold estate with a specific beginning and ending date",
        "rationale": "This describes an estate for years, not an estate from period to period.",
        "isCorrect": false
      },
      {
        "text": "A leasehold estate that can be terminated at any time",
        "rationale": "This describes an estate at will, not an estate from period to period.",
        "isCorrect": false
      },
      {
        "text": "A leasehold estate that lasts forever",
        "rationale": "This describes fee simple absolute, not an estate from period to period.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a lease that continues automatically unless terminated."
  },
  {
    "questionNumber": 133,
    "question": "What is an estate at sufferance in real estate?",
    "answerOptions": [
      {
        "text": "A leasehold estate where the tenant remains after the lease expires",
        "rationale": "An estate at sufferance occurs when a tenant remains after the lease expires.",
        "isCorrect": true
      },
      {
        "text": "A leasehold estate with a specific beginning and ending date",
        "rationale": "This describes an estate for years, not an estate at sufferance.",
        "isCorrect": false
      },
      {
        "text": "A leasehold estate that can be terminated at any time",
        "rationale": "This describes an estate at will, not an estate at sufferance.",
        "isCorrect": false
      },
      {
        "text": "A leasehold estate that renews automatically",
        "rationale": "This describes an estate from period to period, not an estate at sufferance.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what happens when a tenant stays after their lease ends."
  },
  {
    "questionNumber": 134,
    "question": "What is a condominium in real estate?",
    "answerOptions": [
      {
        "text": "Individual ownership of a unit plus shared ownership of common areas",
        "rationale": "A condominium involves individual ownership of a unit plus shared ownership of common areas.",
        "isCorrect": true
      },
      {
        "text": "Individual ownership of a unit only",
        "rationale": "Condominium ownership includes shared ownership of common areas.",
        "isCorrect": false
      },
      {
        "text": "Shared ownership of all areas",
        "rationale": "Condominium ownership includes individual ownership of units.",
        "isCorrect": false
      },
      {
        "text": "Rental of a unit only",
        "rationale": "Condominium ownership involves ownership, not rental.",
        "isCorrect": false
      }
    ],
    "hint": "Think about ownership that combines individual and shared elements."
  },
  {
    "questionNumber": 135,
    "question": "What is a cooperative in real estate?",
    "answerOptions": [
      {
        "text": "Ownership of shares in a corporation that owns the building",
        "rationale": "A cooperative involves ownership of shares in a corporation that owns the building.",
        "isCorrect": true
      },
      {
        "text": "Individual ownership of a unit plus shared ownership of common areas",
        "rationale": "This describes a condominium, not a cooperative.",
        "isCorrect": false
      },
      {
        "text": "Rental of a unit only",
        "rationale": "Cooperative ownership involves ownership, not rental.",
        "isCorrect": false
      },
      {
        "text": "Shared ownership of all areas",
        "rationale": "Cooperative ownership involves individual share ownership.",
        "isCorrect": false
      }
    ],
    "hint": "Think about ownership through corporate shares rather than direct property ownership."
  },
  {
    "questionNumber": 136,
    "question": "What is a timeshare in real estate?",
    "answerOptions": [
      {
        "text": "Shared ownership of property for specific time periods",
        "rationale": "A timeshare involves shared ownership of property for specific time periods.",
        "isCorrect": true
      },
      {
        "text": "Individual ownership of a unit plus shared ownership of common areas",
        "rationale": "This describes a condominium, not a timeshare.",
        "isCorrect": false
      },
      {
        "text": "Ownership of shares in a corporation that owns the building",
        "rationale": "This describes a cooperative, not a timeshare.",
        "isCorrect": false
      },
      {
        "text": "Rental of a unit only",
        "rationale": "Timeshare ownership involves ownership, not rental.",
        "isCorrect": false
      }
    ],
    "hint": "Think about ownership that's divided by time periods."
  },
  {
    "questionNumber": 137,
    "question": "What is a planned unit development (PUD) in real estate?",
    "answerOptions": [
      {
        "text": "A development with individual lots and shared common areas",
        "rationale": "A PUD is a development with individual lots and shared common areas.",
        "isCorrect": true
      },
      {
        "text": "Individual ownership of a unit plus shared ownership of common areas",
        "rationale": "This describes a condominium, not a PUD.",
        "isCorrect": false
      },
      {
        "text": "Ownership of shares in a corporation that owns the building",
        "rationale": "This describes a cooperative, not a PUD.",
        "isCorrect": false
      },
      {
        "text": "Shared ownership of property for specific time periods",
        "rationale": "This describes a timeshare, not a PUD.",
        "isCorrect": false
      }
    ],
    "hint": "Think about a development that combines individual lots with shared amenities."
  },
  {
    "questionNumber": 138,
    "question": "What is a homeowners association (HOA) in real estate?",
    "answerOptions": [
      {
        "text": "An organization that manages common areas and enforces rules",
        "rationale": "An HOA manages common areas and enforces rules in a development.",
        "isCorrect": true
      },
      {
        "text": "An organization that manages individual lots",
        "rationale": "An HOA manages common areas, not individual lots.",
        "isCorrect": false
      },
      {
        "text": "An organization that manages commercial properties",
        "rationale": "An HOA manages residential developments, not commercial properties.",
        "isCorrect": false
      },
      {
        "text": "An organization that manages rental properties",
        "rationale": "An HOA manages developments, not rental properties.",
        "isCorrect": false
      }
    ],
    "hint": "Think about organizations that manage shared areas in residential developments."
  },
  {
    "questionNumber": 139,
    "question": "What are HOA fees in real estate?",
    "answerOptions": [
      {
        "text": "Regular payments made by homeowners to maintain common areas and services",
        "rationale": "HOA fees are regular payments made by homeowners to maintain common areas and services.",
        "isCorrect": true
      },
      {
        "text": "One-time payments made when buying a home",
        "rationale": "HOA fees are ongoing payments, not one-time payments.",
        "isCorrect": false
      },
      {
        "text": "Payments made only when violations occur",
        "rationale": "HOA fees are regular payments, not violation-based payments.",
        "isCorrect": false
      },
      {
        "text": "Payments made only by renters",
        "rationale": "HOA fees are paid by homeowners, not renters.",
        "isCorrect": false
      }
    ],
    "hint": "Think about ongoing payments for community maintenance."
  },
  {
    "questionNumber": 140,
    "question": "What are HOA restrictions in real estate?",
    "answerOptions": [
      {
        "text": "Rules and regulations that govern property use and appearance",
        "rationale": "HOA restrictions are rules and regulations that govern property use and appearance.",
        "isCorrect": true
      },
      {
        "text": "Rules that govern only commercial properties",
        "rationale": "HOA restrictions apply to residential properties, not commercial properties.",
        "isCorrect": false
      },
      {
        "text": "Rules that govern only rental properties",
        "rationale": "HOA restrictions apply to all properties in the development, not just rentals.",
        "isCorrect": false
      },
      {
        "text": "Rules that govern only common areas",
        "rationale": "HOA restrictions apply to individual properties, not just common areas.",
        "isCorrect": false
      }
    ],
    "hint": "Think about rules that control how properties can be used and maintained."
  },

  // BATCH 8: National Real Estate - Property Value & Appraisal (Questions 141-150)
  {
    "questionNumber": 141,
    "question": "What is market value in real estate?",
    "answerOptions": [
      {
        "text": "The most probable price a property would sell for in a competitive market",
        "rationale": "Market value is the most probable price a property would sell for in a competitive market.",
        "isCorrect": true
      },
      {
        "text": "The price a property actually sold for",
        "rationale": "This describes sale price, not market value.",
        "isCorrect": false
      },
      {
        "text": "The price a property is listed for",
        "rationale": "This describes listing price, not market value.",
        "isCorrect": false
      },
      {
        "text": "The price a property is assessed for taxes",
        "rationale": "This describes assessed value, not market value.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the most likely selling price in a fair market."
  },
  {
    "questionNumber": 142,
    "question": "What is assessed value in real estate?",
    "answerOptions": [
      {
        "text": "The value assigned to property for tax purposes",
        "rationale": "Assessed value is the value assigned to property for tax purposes.",
        "isCorrect": true
      },
      {
        "text": "The most probable price a property would sell for",
        "rationale": "This describes market value, not assessed value.",
        "isCorrect": false
      },
      {
        "text": "The price a property actually sold for",
        "rationale": "This describes sale price, not assessed value.",
        "isCorrect": false
      },
      {
        "text": "The price a property is listed for",
        "rationale": "This describes listing price, not assessed value.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the value used for calculating property taxes."
  },
  {
    "questionNumber": 143,
    "question": "What is appraised value in real estate?",
    "answerOptions": [
      {
        "text": "The value determined by a licensed appraiser",
        "rationale": "Appraised value is the value determined by a licensed appraiser.",
        "isCorrect": true
      },
      {
        "text": "The most probable price a property would sell for",
        "rationale": "This describes market value, not appraised value.",
        "isCorrect": false
      },
      {
        "text": "The value assigned to property for tax purposes",
        "rationale": "This describes assessed value, not appraised value.",
        "isCorrect": false
      },
      {
        "text": "The price a property is listed for",
        "rationale": "This describes listing price, not appraised value.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the value determined by a professional appraiser."
  },
  {
    "questionNumber": 144,
    "question": "What is the sales comparison approach in real estate appraisal?",
    "answerOptions": [
      {
        "text": "Comparing the subject property to similar properties that have sold",
        "rationale": "The sales comparison approach compares the subject property to similar properties that have sold.",
        "isCorrect": true
      },
      {
        "text": "Calculating the cost to replace the property",
        "rationale": "This describes the cost approach, not the sales comparison approach.",
        "isCorrect": false
      },
      {
        "text": "Calculating the income the property can generate",
        "rationale": "This describes the income approach, not the sales comparison approach.",
        "isCorrect": false
      },
      {
        "text": "Calculating the property's tax value",
        "rationale": "This describes assessed value, not the sales comparison approach.",
        "isCorrect": false
      }
    ],
    "hint": "Think about comparing similar properties to determine value."
  },
  {
    "questionNumber": 145,
    "question": "What is the cost approach in real estate appraisal?",
    "answerOptions": [
      {
        "text": "Calculating the cost to replace the property minus depreciation",
        "rationale": "The cost approach calculates the cost to replace the property minus depreciation.",
        "isCorrect": true
      },
      {
        "text": "Comparing the subject property to similar properties that have sold",
        "rationale": "This describes the sales comparison approach, not the cost approach.",
        "isCorrect": false
      },
      {
        "text": "Calculating the income the property can generate",
        "rationale": "This describes the income approach, not the cost approach.",
        "isCorrect": false
      },
      {
        "text": "Calculating the property's tax value",
        "rationale": "This describes assessed value, not the cost approach.",
        "isCorrect": false
      }
    ],
    "hint": "Think about calculating what it would cost to build the property today."
  },
  {
    "questionNumber": 146,
    "question": "What is the income approach in real estate appraisal?",
    "answerOptions": [
      {
        "text": "Calculating the income the property can generate",
        "rationale": "The income approach calculates the income the property can generate.",
        "isCorrect": true
      },
      {
        "text": "Comparing the subject property to similar properties that have sold",
        "rationale": "This describes the sales comparison approach, not the income approach.",
        "isCorrect": false
      },
      {
        "text": "Calculating the cost to replace the property",
        "rationale": "This describes the cost approach, not the income approach.",
        "isCorrect": false
      },
      {
        "text": "Calculating the property's tax value",
        "rationale": "This describes assessed value, not the income approach.",
        "isCorrect": false
      }
    ],
    "hint": "Think about calculating value based on rental income potential."
  },
  {
    "questionNumber": 147,
    "question": "What is depreciation in real estate appraisal?",
    "answerOptions": [
      {
        "text": "The loss in value due to wear, tear, and obsolescence",
        "rationale": "Depreciation is the loss in value due to wear, tear, and obsolescence.",
        "isCorrect": true
      },
      {
        "text": "The increase in value over time",
        "rationale": "This describes appreciation, not depreciation.",
        "isCorrect": false
      },
      {
        "text": "The cost to maintain the property",
        "rationale": "This describes maintenance costs, not depreciation.",
        "isCorrect": false
      },
      {
        "text": "The income the property generates",
        "rationale": "This describes rental income, not depreciation.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the loss in value due to aging and wear."
  },
  {
    "questionNumber": 148,
    "question": "What is appreciation in real estate?",
    "answerOptions": [
      {
        "text": "The increase in value over time",
        "rationale": "Appreciation is the increase in value over time.",
        "isCorrect": true
      },
      {
        "text": "The loss in value due to wear, tear, and obsolescence",
        "rationale": "This describes depreciation, not appreciation.",
        "isCorrect": false
      },
      {
        "text": "The cost to maintain the property",
        "rationale": "This describes maintenance costs, not appreciation.",
        "isCorrect": false
      },
      {
        "text": "The income the property generates",
        "rationale": "This describes rental income, not appreciation.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the increase in value over time."
  },
  {
    "questionNumber": 149,
    "question": "What is a comparable property in real estate appraisal?",
    "answerOptions": [
      {
        "text": "A similar property used for comparison in the sales comparison approach",
        "rationale": "A comparable property is a similar property used for comparison in the sales comparison approach.",
        "isCorrect": true
      },
      {
        "text": "A property that generates the same income",
        "rationale": "This describes income properties, not comparable properties.",
        "isCorrect": false
      },
      {
        "text": "A property with the same cost to build",
        "rationale": "This describes cost properties, not comparable properties.",
        "isCorrect": false
      },
      {
        "text": "A property with the same tax value",
        "rationale": "This describes assessed properties, not comparable properties.",
        "isCorrect": false
      }
    ],
    "hint": "Think about similar properties used for comparison."
  },
  {
    "questionNumber": 150,
    "question": "What is a market analysis in real estate?",
    "answerOptions": [
      {
        "text": "An analysis of current market conditions and trends",
        "rationale": "A market analysis examines current market conditions and trends.",
        "isCorrect": true
      },
      {
        "text": "An analysis of a specific property's value",
        "rationale": "This describes a property appraisal, not a market analysis.",
        "isCorrect": false
      },
      {
        "text": "An analysis of rental income potential",
        "rationale": "This describes an income analysis, not a market analysis.",
        "isCorrect": false
      },
      {
        "text": "An analysis of construction costs",
        "rationale": "This describes a cost analysis, not a market analysis.",
        "isCorrect": false
      }
    ],
    "hint": "Think about analyzing overall market conditions and trends."
  }
];
